/**
 * Configuration des Release Notes
 *
 * Ce fichier contient la version actuelle de l'application
 * et l'historique des mises à jour à afficher aux utilisateurs.
 *
 * ## Deux publics, deux lectures
 *
 * Les techniciens Prosoluce et les clients extranet ne lisent pas les mêmes notes.
 * Un item peut donc être masqué (`scope: 'internal'`) ou reformulé (`extranet`).
 * La clé `extranet` porte toujours la version vue par le client — d'un item,
 * d'un titre de groupe ou d'un titre de release.
 *
 *   - Item lu à l'identique par tous :
 *       'Ma feature'
 *   - Item retiré de l'affichage client :
 *       { text: 'Ma feature interne', scope: 'internal' }
 *   - Item montré aux deux, mais formulé autrement côté client :
 *       { text: 'Bascule du picker sur compte_comptes', extranet: 'Choix du fournisseur simplifié' }
 *   - Groupe interne :
 *       { title: 'Admin', items: [...], scope: 'internal' }
 *   - Groupe montré aux deux avec un titre adapté :
 *       { title: 'Compta fournisseur', extranet: 'Vos factures', items: [...] }
 *
 * Un item sans `extranet` est lu tel quel par le client : ne le laisser ainsi que
 * si la formulation interne est déjà présentable. Le filtrage descend dans les
 * groupes, un item interne reste donc masqué même dans un groupe visible.
 *
 * `scope: 'internal'` et `extranet` sont exclusifs : sur un même item, le masquage
 * l'emporte et la reformulation ne sera jamais lue.
 *
 * ATTENTION — `scope: 'internal'` retire l'item de l'affichage, pas du bundle.
 * Ce fichier est livré en entier au navigateur de tous les utilisateurs, clients
 * compris : n'y écrire que ce qu'on peut assumer publiquement. Jamais de nom de
 * table, de serveur, de client, ni la nature d'une faille corrigée.
 */

export const APP_VERSION = '2.48.125';

export const releases = [
  {
    version: '2.48.125',
    date: '2026-11-05',
    title: 'Comptes : préparation des portefeuilles de chargés de compte',
    features: [],
    fixes: [
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          {
            text: 'Ouvrir un compte inexistant ou inaccessible affiche un message et ramène à la liste des comptes, au lieu d’une fiche vide.',
            scope: 'internal',
          },
          {
            text: 'Préparation des portefeuilles de chargés de compte (choix du commercial, création, recherche, rôle directeur commercial). Sans effet pour l’instant.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.124',
    date: '2026-11-02',
    title: 'Factures : le PDF porte son numéro',
    features: [
      {
        title: 'Factures',
        items: [
          {
            text: 'Le bouton PDF enregistre la facture sous son numéro (par exemple FA2510042.pdf) au lieu de l’ouvrir en aperçu sans nom.',
            extranet: 'Vos factures se téléchargent sous leur numéro, par exemple FA2510042.pdf.',
          },
        ],
      },
    ],
    fixes: [],
  },
  {
    version: '2.48.123',
    date: '2026-10-23',
    title: 'Un accès extranet pour plusieurs comptes',
    features: [
      {
        title: 'Accès extranet',
        scope: 'internal',
        items: [
          {
            text: 'Un même accès extranet peut ouvrir plusieurs comptes sans lien de parenté. Depuis la fiche d’un compte, « Configurer l’accès extranet » propose l’interrupteur « Accès à ce compte » : il ouvre ou ferme l’accès à ce compte-là, pourvu que le contact y soit rattaché (par exemple en le créant avec la même adresse e-mail).',
            scope: 'internal',
          },
          {
            text: 'Utilisateurs extranet : un utilisateur ouvert sur plusieurs comptes affiche « +N » à côté du premier, et « Changer de compte » ou « Détacher du compte » ne visent plus que le compte affiché, sans effacer les autres.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [],
  },
  {
    version: '2.48.122',
    date: '2026-10-22',
    title: 'Processus : des dossiers qui s’ouvrent seuls au changement d’état d’un service',
    features: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Modèles de processus : pour un processus de portée service, des règles d’ouverture automatique « quand un service passe de tel état à tel autre, ouvrir un dossier ». Aucune n’existe par défaut ; le dossier ouvert ainsi porte la mention « Automatique ».',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Un même processus ne peut plus avoir deux dossiers en cours pour un même service ou un même compte : la seconde ouverture propose d’aller au dossier existant.',
            scope: 'internal',
          },
          {
            text: 'L’ouverture d’un dossier depuis une demande du support affiche le nom du compte.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.121',
    date: '2026-10-22',
    title: 'Postes SIP : l’accès Mobiluce sur un seul écran',
    features: [
      {
        title: 'Postes SIP — accès Mobiluce',
        scope: 'internal',
        items: [
          {
            text: 'L’onglet Mobiluce de la fiche d’un poste tient sur un seul écran. Un poste ouvert montre le contact qui s’y connecte et avec quoi, et permet sur place de changer l’identifiant, de générer un mot de passe ou de retirer l’accès. Un poste fermé propose trois façons de l’ouvrir : un contact du compte, un nouveau contact, ou un identifiant pour qui n’a pas d’adresse.',
            scope: 'internal',
          },
          {
            text: 'Choisir un contact dans la liste ne l’ouvre plus à Mobiluce sur-le-champ, et vider la liste ne retire plus l’accès sans prévenir : l’ouverture passe par un bouton, le retrait par une confirmation.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Postes SIP — accès Mobiluce',
        scope: 'internal',
        items: [
          {
            text: 'Après avoir rattaché un contact, la fiche l’affiche aussitôt, au lieu d’annoncer « aucun contact lié ».',
            scope: 'internal',
          },
          {
            text: 'Le mot de passe Mobiluce généré depuis la fiche s’affiche de nouveau. Il était bien changé, mais jamais montré.',
            scope: 'internal',
          },
          {
            text: 'Enregistrer un poste ne renomme plus le contact qui s’y connecte avec le nom du poste.',
            scope: 'internal',
          },
          {
            text: 'Une erreur sur l’identifiant (déjà pris, en forme d’adresse, caractère refusé) s’affiche sous le champ avec son motif.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Accès extranet',
        scope: 'internal',
        items: [
          {
            text: 'Un contact qui utilise l’application Mobiluce peut recevoir un accès extranet, sans perdre l’application.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.120',
    date: '2026-10-21',
    title: 'Processus : le fil, les documents et le journal d’un dossier',
    features: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Dossier de processus : trois nouvelles sections à côté des étapes. Le fil pour échanger sur le dossier, les documents pour y joindre bons de commande ou procès-verbaux, et le journal qui dit qui a fait quoi et quand (étapes prises, terminées, bloquées, confiées, dérogations).',
            scope: 'internal',
          },
          {
            text: 'Fiche d’une demande du support : un encart liste les dossiers de processus ouverts depuis elle, et « Ouvrir un dossier » le crée sur son service ou son client. Le dossier garde le lien vers la demande.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'L’ouverture d’un dossier depuis l’onglet Dossiers d’un compte affiche le nom du compte.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.119',
    date: '2026-10-21',
    title: 'Projets : un tableau qui se met à jour à plusieurs',
    features: [],
    fixes: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'Le tableau d’un projet se met à jour seul toutes les 30 secondes : les cartes ajoutées ou déplacées par les autres apparaissent sans recharger la page.',
            scope: 'internal',
          },
          {
            text: 'Une carte modifiée par quelqu’un d’autre pendant qu’on l’avait ouverte n’est plus écrasée : l’enregistrement propose de la recharger.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.118',
    date: '2026-10-16',
    title: 'Processus : les dossiers d’un compte, les étapes à prendre, la vue d’équipe',
    features: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Fiche compte : un onglet « Dossiers » liste les dossiers de processus du compte et de ses services, en cours ou tous, et ouvre un nouveau dossier le client déjà choisi.',
            scope: 'internal',
          },
          {
            text: 'Mes tâches : la bascule « À prendre dans mes équipes » montre les étapes que personne n’a encore reçues dans vos équipes, quand leur tour est venu. « Je m’en occupe » en prend une.',
            scope: 'internal',
          },
          {
            text: 'Vous êtes prévenu quand une étape vous est confiée, puis quand vient son tour dans un processus séquentiel.',
            scope: 'internal',
          },
          {
            text: 'Accueil : le widget « Mon équipe », pour les chefs d’équipe, montre les étapes à prendre, les étapes bloquées et depuis quand, et la charge de chacun.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [],
  },
  {
    version: '2.48.117',
    date: '2026-10-16',
    title: 'Processus : « Je m’en occupe », et des modèles qui ne réécrivent plus les dossiers',
    features: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Étapes de processus : « Je m’en occupe » passe une étape en cours, depuis son dossier ou depuis Mes tâches (bouton lecture). Chacun voit qui l’a prise et depuis quand ; une étape que personne n’avait reçue est confiée à qui la prend. « Lâcher » la remet à faire.',
            scope: 'internal',
          },
          {
            text: 'Une étape bloquée affiche depuis quand, et qui l’a signalée.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Changer l’enchaînement d’un modèle (libre ou séquentiel) ne touche plus les dossiers déjà ouverts.',
            scope: 'internal',
          },
          {
            text: 'Modifier les étapes d’un modèle ne coupe plus le lien des dossiers passés avec leurs étapes, et la version du modèle ne change que si les étapes ont réellement changé.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.116',
    date: '2026-10-16',
    title: 'Projets et processus : changer d’équipe, débloquer une étape',
    features: [
      {
        title: 'Projets et processus',
        scope: 'internal',
        items: [
          {
            text: 'Tableau d’un projet, menu du projet : « Changer d’équipe » le confie à une autre équipe, parmi celles dont vous êtes chef. Les personnes qui perdent l’accès au passage perdent aussi les tâches qui leur étaient confiées, et le transfert est inscrit au journal.',
            scope: 'internal',
          },
          {
            text: 'Dossier de processus : une étape bloquée se débloque depuis son menu ; elle redevient « à faire » et le motif du blocage reste affiché. Un modèle archivé se remet en service par « Réactiver ».',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Projets et processus',
        scope: 'internal',
        items: [
          {
            text: 'Mes tâches : une demande de support s’ouvre dans le support d’un clic, et ne propose plus « je suis dessus », qui échouait. Les tâches des projets archivés n’y figurent plus.',
            scope: 'internal',
          },
          {
            text: 'Retirer quelqu’un d’une équipe ou d’un projet lui retire les tâches qui lui étaient confiées : il continuait d’en recevoir les commentaires et les rappels. Une tâche en retard n’est plus rappelée chaque matin.',
            scope: 'internal',
          },
          {
            text: 'Dossier de processus : les boutons ne s’affichent plus qu’à ceux qui peuvent s’en servir. Un dossier terminé ou annulé ne se modifie plus sans être rouvert, et deux personnes qui règlent en même temps les dernières étapes ne le laissent plus ouvert à 100 %. Enregistrer un modèle n’efface plus son équipe.',
            scope: 'internal',
          },
          {
            text: 'Colonnes : déclarer une colonne terminale termine les tâches qu’elle contient. Le fil de discussion ne saute plus de messages en chargeant les plus anciens. Le journal des projets garde ses filtres sans droit sur les équipes.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.115',
    date: '2026-10-16',
    title: 'Comptes FTP : le mot de passe que vous choisissez',
    extranet: 'Comptes FTP : le mot de passe que vous choisissez',
    features: [],
    fixes: [
      {
        title: 'Hébergement',
        extranet: 'Mon hébergement',
        items: [
          {
            text: 'Un compte FTP se crée avec le mot de passe choisi, et se modifie de même, avec un bouton pour en générer un. Il n’y a plus d’adresse de destination à saisir : aucun courriel ne part.',
            extranet: 'Vous choisissez vous-même le mot de passe de vos comptes FTP, à la création comme à la modification. Un bouton peut en générer un pour vous.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.114',
    date: '2026-10-14',
    title: 'Création d’un compte : un mail, une seule fiche',
    features: [],
    fixes: [
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          {
            text: 'Créer un compte avec le mail de l’adresse d’un compte sans lien est désormais refusé, avec le motif : la reprendre la rattachait à ce compte, et la modifier changeait les deux. Le mail d’une personne déjà connue la reprend comme avant.',
            scope: 'internal',
          },
          {
            text: 'La fenêtre de création d’un site affiche le motif d’un refus. Elle s’arrêtait jusqu’ici de charger sans rien dire.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.113',
    date: '2026-10-14',
    title: 'Ce que les appelants font de votre menu vocal',
    extranet: 'Ce que vos correspondants font de votre menu vocal',
    features: [
      {
        title: 'Téléphonie fixe',
        items: [
          {
            text: 'Nouvelle section « Choix dans les menus vocaux » dans les statistiques d\'une ligne : pour chaque menu, la part de chaque touche, le nombre d\'appels, et en tête ceux qui n\'ont rien tapé — abandon ou délai dépassé. Les menus sont présentés dans l\'ordre où les appelants les rencontrent, et chaque touche annonce sa destination — « Vers Accueil » — au lieu de la seule nature du nœud. Une touche qui mène à un sous-menu, une plage horaire ou une messagerie garde le libellé de sa nature.',
            extranet: 'Vos statistiques d\'appels montrent désormais ce que font vos correspondants dans votre menu vocal : la part de chaque touche, vers qui elle envoie l\'appel, et combien raccrochent sans rien choisir.',
          },
          {
            text: 'Le niveau de service dit maintenant ce qu\'il mesure. Le délai d\'attente court depuis l\'arrivée de l\'appel : il comprend le message d\'accueil et le menu vocal, qui peuvent à eux seuls dépasser le seuil de vingt secondes. La légende donne, à côté du taux habituel, ce qu\'il vaudrait décompté à partir du moment où un poste est appelé. La jauge, elle, continue d\'afficher la mesure publiée jusqu\'ici : les deux chiffres restent comparables d\'une période à l\'autre.',
            extranet: 'La légende du niveau de service indique désormais la part du délai passée dans votre message d\'accueil et votre menu vocal, et ce que le taux vaudrait sans elle.',
          },
          {
            text: 'Le détail d\'un appel montre le chemin qu\'il a suivi dans le plan d\'appel : les étapes traversées dans l\'ordre, et sur chaque menu la touche appuyée avec le temps mis à la choisir. Les étapes portent les icônes et les couleurs de l\'éditeur de plans d\'appel.',
            scope: 'internal',
          },
          {
            text: 'Les blocs de l\'analyse des appels d\'une ligne se replient un par un. Tout est ouvert à chaque ouverture, et un bouton « Déplier les N blocs repliés » apparaît dans la barre d\'outils dès qu\'il y a de quoi déplier.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.112',
    date: '2026-10-13',
    title: 'Hébergement : des comptes plus faciles à retrouver et à régler',
    features: [
      {
        title: 'Hébergement',
        scope: 'internal',
        items: [
          {
            text: 'À la création d’un compte d’hébergement depuis la fiche d’un client, ses limites se règlent tout de suite : disque, domaines web et mail, boîtes mail, bases de données, zones DNS. Elles sont préremplies avec les valeurs de départ, et un champ vide vaut « illimité ».',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Hébergement',
        scope: 'internal',
        items: [
          {
            text: 'La liste des comptes d’un serveur affiche de nouveau le client de chaque compte rattaché, et la recherche par nom de client le retrouve.',
            scope: 'internal',
          },
          {
            text: 'Dans cette liste, les compteurs de ressources ne sont plus rognés, et les actions de chaque ligne restent alignées d’une ligne à l’autre.',
            scope: 'internal',
          },
          {
            text: 'Un compte créé automatiquement à l’ajout d’un service d’hébergement prend le nom du client au lieu d’un nom technique. Les comptes déjà créés gardent le leur.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.111',
    date: '2026-10-13',
    title: 'Espace client : la téléphonie selon l’offre',
    extranet: 'Votre téléphonie, selon votre offre',
    features: [
      {
        title: 'Espace client — téléphonie',
        extranet: 'Ma téléphonie',
        items: [
          {
            text: 'Les statistiques d’appels de l’espace client accompagnent désormais la téléphonie illimitée. Un compte en téléphonie classique y voit directement sa liste d’appels — filtres, déroulé de chaque appel, export — et sa consommation sur les appels émis.',
            extranet: 'Avec une offre de téléphonie classique, la rubrique affiche directement la liste de vos appels et votre consommation. Les statistiques d’appels accompagnent les offres de téléphonie illimitée.',
          },
          {
            text: 'Les postes d’un compte en téléphonie classique s’affichent en consultation, sans le crayon de modification. Un compte qui a un service illimité garde l’écran complet, sur toutes ses lignes.',
            extranet: 'Avec une offre de téléphonie classique, vos postes s’affichent en consultation ; leur modification accompagne les offres de téléphonie illimitée.',
          },
          {
            text: 'Rien ne change côté interne. Des comptes en téléphonie classique peuvent garder l’écran complet par exception, sur réglage de l’exploitation.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Espace client — téléphonie',
        extranet: 'Ma téléphonie',
        items: [
          {
            text: 'Dans le détail des appels, choisir une issue filtre aussitôt la liste : elle ne changeait qu’au geste suivant, alors que l’export en tenait déjà compte.',
            extranet: 'Dans le détail des appels, choisir une issue filtre aussitôt la liste.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.110',
    date: '2026-10-13',
    title: 'Accès extranet : réservé aux fiches de personnes',
    features: [],
    fixes: [
      {
        title: 'Fiche compte',
        scope: 'internal',
        items: [
          {
            text: 'Dans la liste des contacts, le bouton « Créer un accès extranet » est grisé sur une fiche sans nom ni prénom, avec le motif au survol. Ces fiches sont une adresse du compte, pas une personne : pour donner un accès, créer d’abord un contact au nom de la personne.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.109',
    date: '2026-10-11',
    title: 'Accueil : un seul bandeau de bienvenue',
    features: [],
    fixes: [
      {
        title: 'Accueil',
        scope: 'internal',
        items: [
          {
            text: 'Le bandeau de bienvenue en tête de page disparaît : il faisait doublon avec le widget Bienvenue.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.108',
    date: '2026-10-11',
    title: 'Des pastilles de menu plus sobres',
    features: [
      {
        title: 'Menu',
        scope: 'internal',
        items: [
          {
            text: 'Les pastilles de nouveauté du menu sont redessinées : un compteur discret au bout de la ligne, lisible sur tous les fonds, et un simple point sur l’icône quand la barre est repliée.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.107',
    date: '2026-10-11',
    title: 'Support : retrouver le bon compte, filtrer la liste, joindre le demandeur',
    features: [
      {
        title: 'Support client',
        scope: 'internal',
        items: [
          {
            text: 'La fiche d\'une demande sans compte, venue du site ou d\'un courriel, propose les comptes probables d\'après le téléphone, l\'adresse, la société et le nom donnés par le demandeur, chacun avec ses motifs (ligne SDA, alias, contact). Le rattachement se fait après une confirmation qui nomme le compte : rien n\'est jamais rattaché tout seul.',
            scope: 'internal',
          },
          {
            text: 'La liste des demandes affiche la source de chaque demande et la date de la dernière réponse du support, et se filtre par source et par période d\'ouverture, de dernière réponse et de clôture. Un menu d\'actions rapides sur chaque ligne permet de se l\'attribuer, de changer son statut ou sa priorité, de la clôturer ou de la rouvrir sans ouvrir la fiche.',
            scope: 'internal',
          },
          {
            text: 'La fiche affiche le téléphone et la société du demandeur. Quand il n\'a ni adresse ni accès à l\'espace client, la réponse au demandeur est désactivée au profit d\'une invitation à le rappeler. Le fil nomme l\'agent ou le client qui a écrit.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Recherche',
        scope: 'internal',
        items: [
          {
            text: 'Un contact se trouve en tapant son prénom et son nom ensemble, dans n\'importe quel ordre.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Support client',
        extranet: 'Vos demandes',
        items: [
          {
            text: 'Une demande reçoit sa référence (TIC-…) dès sa création : elle restait vide, dans la liste comme dans l\'objet des courriels de réponse.',
            extranet: 'Chaque demande d\'assistance affiche désormais sa référence, à rappeler dans vos échanges avec le support.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.106',
    date: '2026-10-11',
    title: 'Un message plus simple quand aucun hébergement n\'est associé',
    features: [],
    fixes: [
      {
        title: 'Hébergement',
        items: [
          {
            text: 'L\'écran d\'hébergement vide affiche « Aucun hébergement associé à ce compte », sans citer le nom de l\'outil d\'hébergement.',
            extranet: 'Le message affiché lorsqu\'aucun hébergement n\'est associé à votre compte a été simplifié.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.105',
    date: '2026-10-11',
    title: 'Un accueil plus lisible, et le moyen d\u2019y revenir',
    features: [
      {
        title: 'Accueil',
        scope: 'internal',
        items: [
          {
            text: 'Les widgets ne sont plus pos\u00e9s chacun sur un aplat de couleur : une seule carte claire pour tous, et la teinte du widget ne tient plus qu\u2019\u00e0 son ic\u00f4ne. Les chiffres reprennent l\u2019encre du th\u00e8me, les listes se s\u00e9parent d\u2019un filet et les acc\u00e8s rapides passent en monochrome. Ce qui attend se signale par un point pr\u00e8s du titre, plut\u00f4t que par un anneau autour de la carte.',
            scope: 'internal',
          },
          {
            text: 'Le panneau \u00ab Personnaliser \u00bb propose \u00ab Revenir \u00e0 la disposition par d\u00e9faut \u00bb. Un accueil r\u00e9gl\u00e9 une fois ne profitait plus jamais d\u2019un rangement am\u00e9lior\u00e9 depuis, ni d\u2019un widget mieux dimensionn\u00e9.',
            scope: 'internal',
          },
          {
            text: 'Sur t\u00e9l\u00e9phone et en fen\u00eatre r\u00e9duite, les widgets s\u2019empilent \u00e0 la hauteur de leur contenu : le bandeau d\u2019accueil et les acc\u00e8s rapides s\u2019y coupaient. L\u2019\u00e9cran revient \u00e0 la grille d\u00e8s que la fen\u00eatre est \u00e0 nouveau large.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.104',
    date: '2026-10-11',
    title: 'Des mots de passe générés sans esperluette',
    features: [
      {
        title: 'Comptes SIP',
        scope: 'internal',
        items: [
          {
            text: 'Les mots de passe proposés à la création d’un compte SIP et par le bouton de génération ne contiennent plus d’esperluette « & », que certains postes téléphoniques interprétaient. Ils passent à 12 caractères.',
            scope: 'internal',
          },
          {
            text: 'Il en va de même pour tous les mots de passe que génère le serveur : utilisateurs, contacts, hébergement, accès à l’espace client.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.103',
    date: '2026-10-10',
    title: 'La liste des demandes signale celles que vous n\u2019avez pas vues',
    features: [
      {
        title: 'Support',
        scope: 'internal',
        items: [
          {
            text: 'La liste des demandes montre les plus r\u00e9centes en haut, comme une bo\u00eete de r\u00e9ception.',
            scope: 'internal',
          },
          {
            text: 'Une pastille et l\u2019objet en gras signalent une demande que vous n\u2019avez pas encore ouverte, ou dans laquelle quelqu\u2019un d\u2019autre a \u00e9crit depuis votre derni\u00e8re visite. L\u2019ouvrir les \u00e9teint pour vous seul : chacun garde sa propre pastille.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.102',
    date: '2026-10-10',
    title: 'Le profil d’un compte d’hébergement ne s’écrase plus par accident',
    features: [
      {
        title: 'Hébergement',
        scope: 'internal',
        items: [
          {
            text: 'Le bouton qui change le profil d’un compte d’hébergement disparaît sur les comptes que nous gérons. Leurs limites sont posées sur le compte lui-même : appliquer un profil les remplacerait toutes par celles du profil, sans que rien ne signale la perte. Le bouton reste sur les comptes du serveur que nous ne gérons pas.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.101',
    date: '2026-10-09',
    title: 'Ligne mobile : le hors-forfait jour par jour',
    features: [
      {
        title: 'Téléphonie mobile',
        scope: 'internal',
        items: [
          {
            text: 'Consommation d\'une ligne mobile : en vue journalière, le graphe du hors-forfait montre de nouveau la hauteur de chaque jour (toutes les barres restaient à plat), et le résumé ventile le montant entre appels, SMS, data et achats.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.100',
    date: '2026-10-09',
    title: 'Des pastilles sur tout le menu',
    features: [
      {
        title: 'Menu',
        scope: 'internal',
        items: [
          {
            text: 'Des pastilles de nouveauté apparaissent sur tous les écrans qui reçoivent des notifications : les tickets à traiter de vos guichets (visibles aussi sur « Support » replié), les factures d\'achat non ouvertes, et vos notifications non lues de projets, d\'alertes de stock et de jobs de provisioning. Les deux premières sont partagées par le service, les trois autres sont personnelles, comme la cloche.',
            scope: 'internal',
          },
          {
            text: 'Lire une notification dans la cloche met à jour la pastille aussitôt.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.99',
    date: '2026-10-02',
    title: 'Les touches du softphone se gèrent depuis la fiche du poste',
    features: [
      {
        title: 'Postes SIP',
        scope: 'internal',
        items: [
          {
            text: 'Un onglet Touches, dans la fiche d\'un poste, liste les touches programmées sur l\'application Mobiluce et permet de les créer, de les modifier et de les supprimer : numérotation rapide, supervision d\'un poste interne, numéro d\'astreinte, bascule jour/nuit, connexion aux files d\'attente. Le serveur acceptait déjà ces touches depuis le 22 septembre, mais aucun écran ne permettait d\'en créer.',
            scope: 'internal',
          },
          {
            text: 'L\'onglet n\'apparaît qu\'aux utilisateurs autorisés à consulter les postes SIP, et la création comme la modification demandent le droit de les gérer.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.98',
    date: '2026-10-01',
    title: 'Comptabilité : des textes harmonisés',
    features: [
      {
        title: 'Comptabilité',
        scope: 'internal',
        items: [
          'Les écrans des factures d\'achat et le tableau de bord comptable suivent l\'harmonisation des textes, sans tiret long en incise. Libellés seulement, aucun calcul modifié.',
        ],
      },
    ],
  },
  {
    version: '2.48.97',
    date: '2026-10-01',
    title: 'Des notes de version lisibles',
    features: [],
    fixes: [
      {
        text: 'Dans les notes de version, les corrections et améliorations regroupées par thème s\'affichent en liste, au lieu d\'un texte brut entre accolades.',
        extranet: 'Les notes de version affichent correctement les corrections regroupées par thème.',
      },
    ],
  },
  {
    version: '2.48.96',
    date: '2026-10-01',
    title: 'Des textes harmonisés',
    features: [
      {
        title: 'Textes',
        items: [
          {
            text: 'Les écrans, les messages, les fiches d\'aide et les notes de version n\'utilisent plus le tiret long en incise, et certaines formulations ont été rendues plus sobres. Le sens est inchangé.',
            extranet: 'Les textes et messages de l\'application ont été harmonisés et clarifiés.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.95',
    date: '2026-10-01',
    title: 'Un écran Messagerie pour toutes les adresses mail',
    features: [
      {
        title: 'Messagerie',
        items: [
          {
            text: 'Nouvel onglet « Messagerie » sur la fiche compte, sous « Technique » : un seul écran pour toutes les adresses mail du compte, que le domaine soit sur HestiaCP ou sur l\'ancien système. Boîtes, mots de passe, quotas, redirections, listes de diffusion, catch-all, répondeur et antispam ; sur HestiaCP, seules les boîtes, le mot de passe et le quota sont proposés pour l\'instant.',
            extranet: 'Vous gérez de nouveau vos adresses mail depuis votre espace client, rubrique « Ma messagerie » : boîtes, mots de passe, redirections, listes de diffusion, répondeur et filtre antispam.',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Espace client',
        items: [
          'La rubrique « Mes demandes » retrouve son icône dans le menu.',
          {
            text: 'La rubrique « Mes documents » est retirée de l\'espace client pour le moment ; rien ne change en interne.',
            extranet: 'La rubrique « Mes documents » n\'est plus proposée pour le moment.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.94',
    date: '2026-10-01',
    title: 'Retirer un numéro de la liste « À rappeler »',
    features: [
      {
        title: 'Téléphonie',
        items: [
          {
            text: 'Dans les statistiques d\'une ligne fixe, le bouton « Retirer » sort un correspondant de la liste « À rappeler », avec un motif facultatif. Le retrait vaut pour tout le compte ; le correspondant revient automatiquement dans la liste s\'il appelle de nouveau sans obtenir de réponse. Visible avec le droit correspondant.',
            extranet: 'Vous pouvez retirer un numéro de votre liste « À rappeler » lorsque le rappel n\'est plus nécessaire. Il y revient automatiquement si ce correspondant vous appelle de nouveau sans obtenir de réponse.',
          },
          {
            text: 'L\'écran « Appels manqués et suites données » affiche les correspondants retirés, avec l\'auteur, la date et le motif, et permet de les remettre dans la liste.',
            extranet: 'L\'écran des appels manqués affiche les numéros retirés de la liste et permet de les y remettre.',
          },
          {
            text: 'Les explications de la liste « À rappeler » ont été clarifiées.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.92',
    date: '2026-10-01',
    title: 'Un message d\'indisponibilité qui ne s\'affiche plus à tort',
    features: [],
    fixes: [
      {
        title: 'Fiche compte',
        items: [
          'Le message « Cette section n\'est pas disponible pour ce compte » ne s\'affiche plus en bas de pages qui fonctionnent, comme « Mes informations ».',
        ],
      },
    ],
  },
  {
    version: '2.48.91',
    date: '2026-10-01',
    title: 'Un accueil en couleurs, et cinq nouveaux widgets',
    features: [
      {
        title: 'Accueil',
        scope: 'internal',
        items: [
          {
            text: 'Chaque widget a désormais sa couleur, son icône et ses chiffres en grand ; un anneau ambre l\'entoure quand quelque chose attend. Les accès rapides deviennent des tuiles, chacune de sa couleur.',
            scope: 'internal',
          },
          {
            text: 'Un bandeau d\'accueil ouvre l\'écran : la date, et ce qui vous attend (notifications non lues, tâches en retard, tickets à traiter, postes en défaut), chaque pastille menant à l\'écran concerné.',
            scope: 'internal',
          },
          {
            text: 'Nouveaux widgets pour tout le monde : vos dernières notifications, à lire ou ouvrir sur place, et les nouveautés des dernières versions.',
            scope: 'internal',
          },
          {
            text: 'Selon vos droits : les tickets de vos guichets (à traiter, sans réponse, à vous) et la santé du parc téléphonique (part des postes en ligne, déconnectés, en latence).',
            scope: 'internal',
          },
          {
            text: 'Un accueil déjà réglé garde sa disposition : les nouveaux widgets se signalent dans « Personnaliser ».',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.90',
    date: '2026-10-01',
    title: 'Les alias longs restent dans la fiche',
    features: [],
    fixes: [
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          'Un alias long passe à la ligne au lieu d\'élargir la fiche du compte, qui débordait de son cadre.',
        ],
      },
    ],
  },
  {
    version: '2.48.88',
    date: '2026-09-30',
    title: 'Un compte se retrouve par ses alias',
    features: [
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          {
            text: 'La fiche d\'un compte affiche sous la raison sociale ses alias : sigle, graphie « Saint » d\'un « ST », nom suivi de la ville. On en ajoute d\'un Entrée et on en retire d\'une croix ; ceux proposés automatiquement chaque nuit sont en gris, et un alias proposé que l\'on retire n\'est plus jamais reproposé.',
            scope: 'internal',
          },
          {
            text: 'La barre de recherche interroge les alias : « saint aignan » trouve désormais la « MAIRIE DE ST AIGNAN », et le résultat indique l\'alias qui l\'a fait remonter.',
            scope: 'internal',
          },
          {
            text: 'Renforcement des contrôles d\'accès sur la barre de recherche.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.87',
    date: '2026-09-30',
    title: 'Des liens qui mènent où ils le disent',
    features: [
      {
        title: 'Notes',
        scope: 'internal',
        items: [
          {
            text: 'Corriger l\'adresse d\'un lien dans le texte d\'une note corrige aussi sa destination. Le lien continuait d\'ouvrir l\'adresse d\'origine alors qu\'il affichait la nouvelle.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.86',
    date: '2026-09-30',
    title: 'L\'accueil affiche tout, et se personnalise',
    features: [
      {
        title: 'Accueil',
        scope: 'internal',
        items: [
          {
            text: 'Les widgets ne coupent plus leur contenu. La répartition du chiffre d\'affaires n\'apparaissait pas, la liste des tâches s\'arrêtait à la troisième ligne, les accès rapides débordaient : les hauteurs avaient été fixées sans que l\'écran ait été rendu. Si vous aviez déjà réglé votre accueil, il est remis d\'aplomb sans que votre disposition soit perdue.',
            scope: 'internal',
          },
          {
            text: 'Le bouton « Personnaliser » est désormais toujours là, et ouvre la liste de tous vos widgets : cochez ceux que vous voulez voir, décochez pour les retirer. Il disparaissait jusqu\'ici dès que votre accueil était rempli, et avec lui toute trace que cet écran se règle.',
            scope: 'internal',
          },
          {
            text: 'L\'écran est repris : les accès rapides passent de douze pastilles colorées à des libellés compacts, les chiffres s\'alignent en colonnes, et la couleur ne signale plus qu\'un état : un liseré orangé quand quelque chose attend, vert quand c\'est en ordre, rien au repos. La croix et la poignée d\'un widget n\'apparaissent qu\'au survol.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.85',
    date: '2026-09-30',
    title: 'Luce propose une réponse aux demandes du support',
    features: [
      {
        title: 'Support',
        scope: 'internal',
        items: [
          {
            text: 'À l\'ouverture d\'une demande qui attend une réponse, Luce s\'ouvre sur le côté avec un brouillon de réponse au client. « Copier dans la réponse » le verse dans votre zone de réponse : vous le relisez, le corrigez et l\'envoyez vous-même. Luce n\'envoie jamais rien.',
            scope: 'internal',
          },
          {
            text: '« Régénérer » propose une autre version, avec une indication facultative comme « plus court » ou « plus formel ».',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        title: 'Support',
        scope: 'internal',
        items: [
          {
            text: 'Le fil d\'échanges d\'une demande s\'affiche de nouveau, et les réponses comme les courriels reçus s\'y enregistrent.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.83',
    date: '2026-09-30',
    title: 'La liste des postes d\'un client s\'exporte en CSV',
    features: [
      {
        title: 'Téléphonie',
        scope: 'internal',
        items: [
          {
            text: 'Un bouton Exporter sur la liste des postes de la fiche client produit un fichier CSV, avec le choix des colonnes : nom du poste, adresse MAC, numéro affiché, numéro interne et statut sont cochés d\'office ; identifiant SIP, équipement, site facturé et transport en option. L\'export reprend tous les postes du client en suivant la recherche en cours et l\'interrupteur des comptes supprimés.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.81',
    date: '2026-09-29',
    title: 'Un accueil que chacun dispose comme il veut',
    features: [
      {
        title: 'Accueil',
        scope: 'internal',
        items: [
          {
            text: 'L\'accueil est fait de widgets que vous placez et redimensionnez vous-même : vos tâches, le chiffre d\'affaires du mois, les factures d\'achat à ventiler, les alertes de stock, et les accès rapides, devenus un widget comme les autres. Votre disposition est retenue et vous suit d\'un poste à l\'autre. Vous ne voyez que les widgets dont vous avez les droits, et la disposition se règle sur écran large.',
            scope: 'internal',
          },
          {
            text: 'Chaque widget se charge pour son compte : celui qui met du temps n\'empêche plus les autres de s\'afficher, et celui qui échoue le dit sur son emplacement au lieu de laisser croire qu\'il n\'y a rien à signaler.',
            scope: 'internal',
          },
          {
            text: 'Un bouton « Ajouter un widget » propose ceux que vous n\'avez pas posés et signale ceux qui viennent d\'apparaître. Si un droit vous est retiré, le widget correspondant disparaît en le disant, au lieu de passer pour une panne.',
            scope: 'internal',
          },
          {
            text: 'Les accès « Utilisateurs » et « Droits et rôles » s\'affichent désormais selon les droits qu\'ils demandent, et non selon le titre de super administrateur : un administrateur qui n\'a pas ce titre les voit enfin. Le second ne mène plus sur une page introuvable.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.80',
    date: '2026-09-29',
    title: 'La fiche d\'un compte se relit après un changement d\'adresse',
    features: [
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          {
            text: 'Enregistrer l\'adresse d\'un site peut faire changer de fiche de contact au compte, quand celle-ci était partagée avec d\'autres. La fiche se recharge désormais au lieu de garder des identifiants périmés : enchaîner sur l\'adresse de facturation ne se solde plus par une erreur et une saisie perdue.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.79',
    date: '2026-09-29',
    title: 'Le niveau de service se tait quand un filtre l\'empêche d\'être calculé',
    extranet: 'Le niveau de service indique désormais quand un filtre empêche son calcul',
    features: [
      {
        title: 'Statistiques de téléphonie fixe',
        extranet: 'Statistiques de vos appels',
        items: [
          {
            text: 'La phrase sous le niveau de service n\'annonce plus de chiffres quand la jauge est vide : elle dit quel filtre suspend le calcul, au lieu de commenter un taux que l\'écran refuse d\'afficher. Sa formulation change aussi : « Appels reçus décrochés en moins de 20 secondes : 168 sur 1 029 ». L\'ancienne formulation se lisait comme si ces 1 029 appels avaient tous été décrochés.',
            extranet: 'La mention sous le niveau de service indique désormais pourquoi le taux n\'est pas calculé lorsqu\'un filtre est posé, et sa formulation ne prête plus à confusion.',
          },
          {
            text: 'Sur les appels reçus, sélectionner un poste ne fait plus apparaître « sans réponse : 0 » dans la répartition des appels : un appel reçu sans réponse n\'est rattaché à aucun poste, la part disparaît plutôt que d\'annoncer un zéro que personne n\'a mesuré. Le niveau de service et le tableau des files d\'attente se taisent pour la même raison, dans l\'écran comme dans son export.',
            extranet: 'En filtrant sur un poste, la répartition des appels reçus n\'affiche plus de part « sans réponse » à zéro.',
          },
          {
            text: 'Sur les appels émis et internes, la part des appels aboutis se met en attente lorsqu\'une issue est filtrée : elle valait cent pour cent sur les seuls appels décrochés et zéro sur les seuls manqués, et décrivait le filtre plutôt que la téléphonie.',
            extranet: 'Sur les appels émis, la part des appels aboutis n\'est plus calculée lorsque vous filtrez sur une issue d\'appel.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.78',
    date: '2026-09-29',
    title: 'La fenêtre de renvoi des factures annonce la pièce jointe ou le lien',
    features: [
      {
        title: 'Factures',
        scope: 'internal',
        items: [
          {
            text: 'Avant l\'envoi, la fenêtre de renvoi par mail indique si les factures seront jointes au mail, avec leur poids, ou remplacées par un lien de téléchargement. Les réglages du lien ne s\'affichent que dans ce second cas.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.75',
    date: '2026-09-29',
    title: 'Les factures renvoyées par mail partent de nouveau en pièce jointe',
    features: [
      {
        title: 'Factures',
        scope: 'internal',
        items: [
          {
            text: 'Le renvoi et la relance de factures par mail joignent de nouveau les PDF, comme au premier envoi, au lieu d\'un lien de téléchargement valable 48 h. Le lien ne sert plus qu\'au lot dépassant 10 Mo cumulés.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.74',
    date: '2026-09-29',
    title: 'Les boutons de téléchargement d\'une facture d\'achat ne promettent plus rien',
    features: [
      {
        title: 'Factures d\'achat',
        scope: 'internal',
        items: [
          {
            text: 'Les boutons de téléchargement de la fiche ne s\'affichent plus que si le fichier existe vraiment : un bouton visible télécharge quelque chose.',
            scope: 'internal',
          },
          {
            text: 'Un seul bouton apparaît quand la pièce reçue est déjà le PDF lisible. Le second ne revient que lorsqu\'il mène à un autre document, et annonce alors son format : il est clair avant le clic que l\'on télécharge la pièce probante et non un document de lecture.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.73',
    date: '2026-09-29',
    title: 'La relance d\'une facture par mail n\'est plus interrompue',
    features: [
      {
        title: 'Factures',
        scope: 'internal',
        items: [
          {
            text: 'Le renvoi et la relance d\'une facture par mail ne s\'interrompent plus en « Erreur dans l\'envoi » quand le serveur de messagerie tarde à répondre : l\'écran attend jusqu\'à une minute au lieu de dix secondes.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.72',
    date: '2026-09-29',
    title: 'Le côté de l\'utilisateur est lu tel que le serveur le calcule',
    features: [
      {
        title: 'Espace client',
        scope: 'internal',
        items: [
          {
            text: 'Le sélecteur de comptes de la barre latérale et le centre de notifications lisent le côté de l\'utilisateur tel que le serveur le calcule, au lieu de le déduire du nom d\'un rôle. Sans effet visible aujourd\'hui, un seul profil client existant en base ; la lecture par le nom cessait d\'être juste dès qu\'un second profil était créé.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.71',
    date: '2026-09-29',
    title: 'Le compteur de nouveautés du menu ne chevauche plus le libellé',
    features: [
      {
        title: 'Menu',
        scope: 'internal',
        items: [
          {
            text: 'Le compteur de nouveautés d\'un groupe du menu (Comptabilité, …) se pose de nouveau sur le coin de son icône au lieu de chevaucher le libellé.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.70',
    date: '2026-09-28',
    title: 'L\'espace client s\'ouvre de nouveau après un rafraîchissement de la page',
    features: [
      {
        title: 'Espace client',
        extranet: 'Vos comptes',
        items: [
          {
            text: 'Après un rafraîchissement de la page, l\'ouverture d\'un compte affichait une page d\'erreur au lieu du compte. Seuls les clients rattachés à plusieurs comptes le rencontraient : avec un compte unique, l\'accueil ne tentait pas l\'ouverture, et la panne restait invisible.',
            extranet: 'L\'ouverture d\'un de vos comptes fonctionne de nouveau, y compris après avoir rafraîchi la page.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.69',
    date: '2026-09-28',
    title: 'Les appels d\'un service fixe s\'affichent de nouveau',
    features: [
      {
        title: 'Téléphonie',
        scope: 'internal',
        items: [
          {
            text: 'L\'écran des appels d\'un service fixe s\'ouvre de nouveau. Il affichait deux messages d\'erreur à la place des statistiques, alors que le serveur renvoyait bien les données.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.68',
    date: '2026-09-28',
    title: 'Statistiques mobiles : les lignes sans montant sortent du lot',
    features: [
      {
        title: 'Statistiques mobiles',
        scope: 'internal',
        items: [
          {
            text: 'Une ligne facturée sans montant n\'est plus confondue avec une ligne qui perd de l\'argent : le bandeau les compte, la vue par client les filtre, et le tableau les marque « sans montant ». Ce sont des lignes offertes, ou un montant oublié à la fiche du service.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.67',
    date: '2026-09-25',
    title: 'Les repères de nouveauté, en discret',
    features: [
      {
        title: 'Menu',
        scope: 'internal',
        items: [
          {
            text: 'Le compteur de nouveautés se pose désormais sur le coin de l\'icône du module, en petit et aux couleurs de l\'application, au lieu du gros rond rouge accolé au libellé. Il garde la même place barre dépliée ou repliée, et le compteur du module parent s\'efface une fois ses pages dépliées : le même chiffre n\'apparaît plus deux fois à l\'écran.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Factures d\'achat',
        scope: 'internal',
        items: [
          {
            text: 'Dans la liste, une facture que personne du service n\'a encore ouverte se signale par un point devant son numéro, écrit en gras, au lieu de l\'étiquette rouge qui décalait la colonne. Une infobulle rappelle ce que le point veut dire.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.66',
    date: '2026-09-25',
    title: 'Le support : vos demandes, les résolutions passées, les chiffres',
    features: [
      {
        title: 'Support',
        scope: 'internal',
        items: [
          {
            text: 'Les demandes qui vous sont affectées apparaissent dans « Mes tâches », à côté des cartes de projet et des étapes de processus, et s\'y rangent dans votre ordre personnel.',
            scope: 'internal',
          },
          {
            text: 'Sur une demande ouverte, un bloc « Déjà résolu ailleurs » propose les demandes résolues qui lui ressemblent, avec ce qui avait été fait. La recherche part des mots de la demande.',
            scope: 'internal',
          },
          {
            text: 'Un écran de statistiques : demandes reçues, encore ouvertes, taux de résolution, délais de première réponse et de résolution, et ce qui n\'a jamais reçu de réponse. Les délais se comptent en heures ouvrées, et chaque chiffre dit ce qu\'il compte.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.65',
    date: '2026-09-25',
    title: 'Statistiques mobiles : des chiffres qui disent leur durée',
    features: [
      {
        title: 'Statistiques mobiles',
        scope: 'internal',
        items: [
          {
            text: 'Chaque montant annonce la durée sur laquelle il porte : « Facturé (12 mois) », « Coût sur 3 mois ». Un total de douze mois lu comme une mensualité donnait une marge de manœuvre douze fois trop grande.',
            scope: 'internal',
          },
          {
            text: 'La « remise possible » remplace la « remise max » : marge mensuelle moyenne des six derniers mois de la ligne, en euros par mois, indépendante de la période affichée. Par client, ce qu\'il y a à renégocier chaque mois s\'affiche sous sa marge.',
            scope: 'internal',
          },
          {
            text: 'Une courbe des douze derniers mois (facturé, coût, marge) sur le périmètre consulté.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.64',
    date: '2026-09-25',
    title: 'Luce : un réglage de moins',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          {
            text: 'Le choix du modèle disparaît de la barre : il n\'y avait rien à y régler et la réponse n\'en dépendait pas. Le modèle est décidé côté serveur, et reste indiqué sous chaque réponse.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.63',
    date: '2026-09-25',
    title: 'Statistiques mobiles : ce que le coût recouvre',
    features: [
      {
        title: 'Statistiques mobiles',
        scope: 'internal',
        items: [
          {
            text: 'La vue détaillée dit maintenant ce que le coût d\'une ligne recouvre : sa consommation, un minimum de facturation quand elle consomme peu, et aucun abonnement fournisseur (les grilles d\'achat n\'en portent pas). Une marge ne se lit pas de la même façon quand on sait ce qu\'elle ne compte pas.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.62',
    date: '2026-09-25',
    title: 'Un justificatif annoncé est un justificatif qui s\'ouvre',
    features: [
      {
        title: 'Factures d\'achat',
        scope: 'internal',
        items: [
          {
            text: 'Dans la liste, le repère de justificatif ne s\'allume que si le document peut réellement être ouvert. Il s\'affichait jusqu\'ici sur toute pièce à laquelle un justificatif avait été rattaché un jour, y compris quand le document avait été retiré depuis : le téléchargement échouait alors sans que rien ne l\'ait laissé prévoir.',
            scope: 'internal',
          },
          {
            text: 'Un troisième état, orange, distingue la pièce dont le justificatif est rattaché mais introuvable de celle qui n\'en a jamais eu. Une infobulle dit lequel des trois cas s\'applique.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.61',
    date: '2026-09-25',
    title: 'Les réponses types, à portée de clic',
    features: [
      {
        title: 'Support',
        scope: 'internal',
        items: [
          {
            text: 'Un sélecteur « Insérer une réponse type » au-dessus de la zone de réponse : le texte arrive avec le demandeur, la référence et l\'objet déjà remplis, et reste modifiable. Il s\'ajoute à ce qui est déjà écrit, sans l\'effacer.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.60',
    date: '2026-09-24',
    title: 'Le support se règle en un écran',
    features: [
      {
        title: 'Paramètres',
        scope: 'internal',
        items: [
          {
            text: 'Un écran « Support » réunit les guichets, les réponses types et les consignes de l\'assistant. Une réponse type peut viser un guichet ou valoir pour tous ; ses variables (demandeur, référence, objet, agent) sont remplies au moment où l\'agent l\'insère.',
            scope: 'internal',
          },
          {
            text: 'Les consignes de l\'assistant sont versionnées : écrire crée une version, l\'activer met la précédente de côté sans l\'effacer, et une version antérieure se remet en vigueur. Chacune porte une note sur ce qu\'elle change.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.59',
    date: '2026-09-24',
    title: 'La clé d’accès d’un serveur d’hébergement se choisit dans le coffre',
    features: [
      {
        title: 'Hébergement',
        scope: 'internal',
        items: [
          {
            text: 'La fiche d’un serveur ne demande plus d’y coller une clé d’accès. La liste indique à la place si le serveur est rattaché à l’équipement déclaré dans le provisionnement, et une fenêtre permet de l’y rattacher ou de l’en détacher. La clé est ainsi gardée au même endroit que celles du reste du parc.',
            scope: 'internal',
          },
          {
            text: 'Un serveur qui n’est rattaché à aucun équipement refuse la création de comptes en le disant, là où le message parlait d’une clé absente sans indiquer où la poser. Un équipement devenu inutilisable s’affiche en rouge au lieu de passer pour valide.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.58',
    date: '2026-09-23',
    title: 'La fiche compte parle de code comptable, et n\'en montre que ce qui sert',
    features: [
      {
        title: 'Fiche compte',
        scope: 'internal',
        items: [
          {
            text: 'Le « compte auxiliaire » s\'appelle désormais « code comptable ». Chaque code ne s\'affiche que pour le rôle qui le porte : un compte seulement fournisseur ne propose plus de code client, un compte seulement client plus de code fournisseur, un compte à double rôle montre les deux. Un code déjà enregistré garde son champ visible, pour rester corrigeable.',
            scope: 'internal',
          },
          {
            text: 'Le champ « compte de charge par défaut » du fournisseur est retiré : un même fournisseur se ventile selon la nature de l\'achat, il n\'y a pas de compte à poser d\'avance sur sa fiche. La ventilation des factures d\'achat continue de proposer le compte sur lequel ce fournisseur a déjà été ventilé.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.57',
    date: '2026-09-23',
    title: 'Les guichets du support se règlent, et les audits se rejoignent',
    features: [
      {
        title: 'Paramètres',
        scope: 'internal',
        items: [
          {
            text: 'Un écran « Guichets du support » : libellé, code, adresse mail d\'arrivée, priorité par défaut, visibilité côté client, ordre et activation. Créer un guichet crée son droit de lecture, le supprimer l\'emporte avec lui, et un guichet qui porte des demandes se désactive au lieu d\'être supprimé.',
            scope: 'internal',
          },
          {
            text: 'Les deux journaux d\'audit tiennent sur une seule ligne de menu, en deux vues. Les anciennes adresses mènent toujours au bon journal.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.56',
    date: '2026-09-23',
    title: 'Un projet peut réunir plusieurs équipes',
    features: [
      {
        title: 'Gestion de projet',
        scope: 'internal',
        items: [
          {
            text: 'Le menu d\'un tableau ouvre « Qui travaille dessus… » : on y rattache d\'autres équipes, quand deux services mènent le travail ensemble, et des personnes seules. Une personne rattachée n\'entre dans aucune équipe (elle voit ce projet et lui seul) mais y travaille comme un membre : cartes, commentaires, tâches confiées. Le créateur du projet et ceux qui l\'administrent décident des rattachements.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.55',
    date: '2026-09-23',
    title: 'Support : la barre des guichets et le rattachement d\'un ticket',
    features: [
      {
        title: 'Support',
        scope: 'internal',
        items: [
          {
            text: 'Les guichets passent sur la barre bleue du reste de l\'application : l\'onglet ouvert se voit enfin, chaque guichet porte son icône, et la barre suit les guichets déclarés en base.',
            scope: 'internal',
          },
          {
            text: 'Le compte d\'un ticket se change depuis sa fiche : une adresse d\'expéditeur ne désigne pas un client, et un contact partagé en engage plusieurs. Un compte que le demandeur n\'engage pas est refusé.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.54',
    date: '2026-09-23',
    title: 'Support client : confier une demande, y joindre un service',
    features: [
      {
        title: 'Support client',
        scope: 'internal',
        items: [
          {
            text: 'Une demande se confie à un collègue depuis sa fiche : la liste ne propose que les personnes qui suivent le guichet concerné, et se cherche au fil de la frappe. Se l\'attribuer ou retirer l\'affectation reste à portée de clic.',
            scope: 'internal',
          },
          {
            text: 'Les services concernés par une demande se complètent depuis la fiche. La liste ne montre que les services du client, écarte ceux déjà rattachés dans le rôle choisi (un même service peut être signalé par le client puis mis en cause après diagnostic), et prévient quand elle est trop longue pour tout afficher.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.53',
    date: '2026-09-23',
    title: 'Les demandes d\'un client, dans sa fiche',
    features: [
      {
        title: 'Support',
        scope: 'internal',
        items: [
          {
            text: 'La fiche d\'un compte montre ses demandes de support : objet, guichet, priorité, statut et ancienneté. Depuis la fiche d\'une maison mère, celles de ses sites sont incluses. Un clic ouvre la demande.',
            scope: 'internal',
          },
          {
            text: 'Une demande sans première réponse depuis plus d\'une journée ouvrée est signalée aux agents de son guichet, une seule fois, et la liste sait les isoler par la vue « Sans réponse ». La priorité reste ce qu\'une personne en a décidé : rien ne la modifie automatiquement.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.52',
    date: '2026-09-23',
    title: 'Comptes : l\'adresse d\'un site partagé se corrige de nouveau',
    features: [
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          {
            text: 'Quand plusieurs comptes se partagent une même fiche de contact, l\'écran de l\'adresse du site refusait toute modification, sans rien proposer d\'autre. Il pose désormais la question : modifier l\'adresse pour tous les comptes concernés, qu\'il nomme, ou donner à ce compte-ci sa propre fiche. Dans ce second cas, seule l\'adresse du site le suit : la facturation et les contacts technique et administratif restent là où ils sont.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.51',
    date: '2026-09-23',
    title: 'Vos demandes d\'assistance, dans votre espace',
    features: [
      {
        title: 'Support',
        extranet: 'Vos demandes',
        items: [
          {
            text: 'L\'espace client reçoit une section « Mes demandes » : la liste des demandes du compte, leur avancement, et le fil des échanges avec nos équipes.',
            extranet: 'Retrouvez vos demandes d\'assistance dans « Mes demandes » : leur avancement et nos échanges, au même endroit.',
          },
          {
            text: 'Le client dépose une demande depuis cet écran, en choisissant le guichet, et répond dans le fil tant qu\'elle n\'est pas close.',
            extranet: 'Déposez une nouvelle demande et répondez-nous directement depuis le fil. Une demande close se remplace par une nouvelle.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.50',
    date: '2026-09-23',
    title: 'Gestion de projet : Entrée publie le message',
    features: [
      {
        title: 'Gestion de projet',
        scope: 'internal',
        items: [
          {
            text: 'Dans le fil d\'un projet comme dans le commentaire d\'une carte, la touche Entrée publie le message et Maj + Entrée passe à la ligne. Il fallait jusqu\'ici Ctrl + Entrée, ou le bouton : un message écrit puis validé d\'un Entrée n\'était jamais envoyé, sans le moindre avertissement. Un message resté non publié est désormais retrouvé en rouvrant le fil.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.49',
    date: '2026-09-23',
    title: 'Support client : la fiche d\'un ticket',
    features: [
      {
        title: 'Support client',
        scope: 'internal',
        items: [
          {
            text: 'Une ligne de la liste ouvre la fiche du ticket : le fil des échanges, où l\'on répond au demandeur ou l\'on prend une note interne, avec des pièces jointes des deux côtés. Une pièce reçue qui n\'a pas passé l\'analyse antivirus n\'est pas proposée au téléchargement.',
            scope: 'internal',
          },
          {
            text: 'Depuis la fiche : passer le ticket « en cours » ou « en attente du client », se l\'attribuer ou s\'en retirer, ajouter quelqu\'un en copie, et voir les services concernés. La clôture demande toujours un motif, et la solution rédigée quand la demande est déclarée résolue.',
            scope: 'internal',
          },
          {
            text: 'Sur la liste des tickets, le titre de l\'écran et le bouton « Nouveau ticket » n\'apparaissaient pas : c\'est réparé.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.48',
    date: '2026-09-23',
    title: 'Gestion des droits : un écran plus réactif',
    features: [
      {
        title: 'Gestion des droits',
        scope: 'internal',
        items: [
          {
            text: 'L\'écran s\'ouvre et se fouille sans attente : le sélecteur de niveau d\'une cellule n\'est construit qu\'au moment où on l\'ouvre, et la recherche attend une pause dans la frappe avant de filtrer.',
            scope: 'internal',
          },
          {
            text: 'Un droit se cherche par le mot qu\'on a en tête : « hébergement » trouve les droits HestiaCP, « prélèvement », « appels » ou « support » font de même.',
            scope: 'internal',
          },
          {
            text: 'Quand rien ne correspond dans l\'onglet ouvert, le message dit dans quel onglet chercher.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.47',
    date: '2026-09-23',
    title: 'Les documents d\'un projet',
    features: [
      {
        title: 'Gestion de projet',
        scope: 'internal',
        items: [
          {
            text: 'Sous la description d\'un projet, un bloc « Documents » : on y joint des fichiers, on les télécharge, on les retire. Ils sont rangés dans la GED, avec ses contrôles de type et de taille, sans jamais se mêler aux documents des clients. Chacun retire ses propres dépôts ; qui peut modifier le projet les retire tous.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.46',
    date: '2026-09-23',
    title: 'Clés d\'API : des droits choisis',
    features: [
      {
        title: 'Administration',
        scope: 'internal',
        items: [
          {
            text: 'Une clé d\'API serveur se crée désormais avec la liste de ce qu\'elle a le droit de faire, affichée dans le tableau des clés. Les clés existantes, marquées « Historique », gardent exactement leurs accès d\'origine.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.45',
    date: '2026-09-22',
    title: 'Gestion des droits : une matrice qui se fouille',
    features: [
      {
        title: 'Gestion des droits',
        scope: 'internal',
        items: [
          {
            text: 'Un champ de recherche filtre les droits à la frappe et déplie ce qu\'il trouve. Un droit rangé dans un autre onglet est signalé : chercher « hestia » dans la matrice interne indique qu\'il se règle dans l\'onglet Infrastructure.',
            scope: 'internal',
          },
          {
            text: 'Chaque droit s\'affiche par sa description, et la rubrique « Autres » ne liste plus les droits inattribuables hérités d\'un ancien paramétrage.',
            scope: 'internal',
          },
          {
            text: 'Le menu Hébergement s\'ouvre au rôle « admin hébergement ».',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.44',
    date: '2026-09-22',
    title: 'Support client : les tickets',
    features: [
      {
        title: 'Support client',
        scope: 'internal',
        items: [
          {
            text: 'Un nouveau menu Support rassemble les tickets des guichets que vous lisez : un onglet par guichet, la vue « À traiter » par défaut, des filtres par priorité et par affectation (moi, personne), et une recherche sur la référence ou l\'objet.',
            scope: 'internal',
          },
          {
            text: 'Un ticket se crée depuis cet écran : guichet, canal, objet, priorité, compte et demandeur. Cochée, la case « Ticket entre salariés » le garde hors de tout espace client.',
            scope: 'internal',
          },
          {
            text: 'Les droits se donnent dans la matrice, domaine « Support client » : un droit de lecture par guichet, et la gestion des tickets à part.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.43',
    date: '2026-09-22',
    title: 'Mes tâches : « je suis dessus »',
    features: [
      {
        title: 'Gestion de projet',
        scope: 'internal',
        items: [
          {
            text: 'Dans Mes tâches, un geste marque une tâche « je suis dessus », la met en pause, la reprend ou la retire, y compris plusieurs à la fois. L\'état est personnel : il s\'affiche chez vous, dans Mes tâches et sur la carte du kanban, et nulle part chez vos collègues. Il s\'efface de lui-même quand la tâche est terminée.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.42',
    date: '2026-09-22',
    title: 'Statistiques mobiles : un écran repris pour décider',
    features: [
      {
        title: 'Statistiques mobiles',
        scope: 'internal',
        items: [
          {
            text: 'La recherche d\'un client ne renvoyait plus rien depuis fin avril. Elle cherche de nouveau par nom, raison sociale ou libellé d\'une ligne mobile. La recherche par client du stock de numéros, tombée en panne en même temps, est également réparée.',
            scope: 'internal',
          },
          {
            text: 'Période et périmètre se choisissent dans un bandeau qui répond en une phrase : combien de lignes, quelle marge, quelle part du facturé part en coût. Les raccourcis ne portent que sur des mois complets, car les chiffres d\'un mois sont calculés le 2 du mois suivant.',
            scope: 'internal',
          },
          {
            text: 'La vue par client range chaque compte sur une échelle de marge, pertes à gauche, gains à droite. On filtre à la frappe par client, numéro ou libellé, et un clic ouvre les lignes du client dans un volet.',
            scope: 'internal',
          },
          {
            text: 'Une vue commerciale, sans opérateur ni condition d\'achat, s\'ouvre par défaut. La vue détaillée demande le droit « Voir le détail d\'achat des statistiques mobiles », à cocher dans la matrice.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.41',
    date: '2026-09-22',
    title: 'Menu : libellés alignés',
    features: [
      {
        title: 'Menu',
        items: [
          'Les libellés du menu de gauche sont alignés : ceux qui ouvrent une liste de pages ne sont plus décalés vers la droite.',
          {
            text: 'Provisioning a sa propre icône, distincte de celle d\'Hébergement.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.40',
    date: '2026-09-22',
    title: 'Projets : une équipe se crée depuis le projet',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'La création d\'un projet propose « Nouvelle équipe… » : l\'équipe qui portera le projet se monte sans quitter la fenêtre, et elle est aussitôt choisie.',
            scope: 'internal',
          },
          {
            text: 'Un double clic sur « Créer » ne crée plus deux fois la même équipe.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.39',
    date: '2026-09-22',
    title: 'Téléphonie : l\'export CSV des appels est rétabli',
    features: [
      {
        title: 'Téléphonie',
        items: [
          {
            text: '« Exporter en CSV » livre de nouveau la liste des appels. Le fichier téléchargé contenait une page web au lieu du tableau, dans l\'espace client comme sur l\'écran interne (export des appels et des files d\'attente).',
            extranet: 'L\'export CSV de vos appels, depuis le tableau de bord de votre téléphonie, produit de nouveau un tableau exploitable dans votre tableur.',
          },
        ],
      },
      {
        title: 'Factures',
        scope: 'internal',
        items: [
          {
            text: 'Au renvoi d\'une facture par lien, un motif « Relance de règlement » propose un titre et un message de rappel prêts à l\'envoi, au lieu de la formule « Suite à votre demande » qu\'il fallait effacer et retaper à chaque relance.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.38',
    date: '2026-09-21',
    title: 'Un menu plus simple',
    features: [
      {
        title: 'Navigation',
        scope: 'internal',
        items: [
          {
            text: 'Le menu de gauche range un module par ligne, sur deux niveaux au plus : Comptes, Facturation, Comptabilité, Stock, Téléphonie, Hébergement, Provisioning, Projets, Statistiques, Paramètres. Plus de rubrique à déplier trois fois, ni de doublon comme « Téléphonie > Téléphonie ».',
            scope: 'internal',
          },
          {
            text: 'Chacun ne voit toujours que les écrans auxquels ses droits donnent accès. Quand un module se réduit à un seul écran, le menu y mène directement.',
            scope: 'internal',
          },
          {
            text: 'La matrice des droits suit le même rangement que le menu, et Luce connaît les nouveaux chemins.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.37',
    date: '2026-09-21',
    title: 'Téléphonie : les courbes suivent le sens affiché',
    features: [
      {
        title: 'Téléphonie',
        items: [
          {
            text: 'Sur le tableau de bord de la téléphonie, les courbes du temps de conversation, des appels décrochés et de l\'attente suivent le sens affiché, reçus ou émis, comme le chiffre annoncé au-dessus. Elles mêlaient tous les sens, si bien que la somme de leurs points dépassait le total.',
            extranet: 'Sur le tableau de bord de votre téléphonie, les courbes ne portent plus que les appels reçus ou émis, selon l\'onglet choisi : le temps de conversation qu\'elles additionnent correspond désormais au total affiché au-dessus.',
          },
        ],
      },
      {
        title: 'Affichage',
        scope: 'internal',
        items: [
          {
            text: 'Mode sombre : le bandeau de la fiche d\'un compte et celui de la gestion des numéros fixes prennent enfin leur fond sombre. Ils restaient blancs, avec un texte clair devenu illisible.',
            scope: 'internal',
          },
          {
            text: 'Sur un écran étroit, la barre d\'onglets de la fiche d\'un compte et celle de la gestion des droits défilent de nouveau au lieu de déborder.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.36',
    date: '2026-09-17',
    title: 'Gestion de projet : monter une équipe devient possible',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'Le bouton « Nouvelle équipe » s\'affiche enfin. Il dépendait d\'un droit d\'administration que personne ne portait : aucune équipe ne pouvait naître, donc aucun projet. Monter une équipe est désormais un droit à part, ouvert à tous les rôles internes : celui qui la crée en devient chef, la renomme et en compose les membres. Administrer les équipes des autres reste réservé.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Droits',
        scope: 'internal',
        items: [
          {
            text: 'Les trois droits des processus de service apparaissent dans la matrice des rôles, sous leur propre écran. Ils y étaient invisibles depuis leur création, donc impossibles à accorder à qui que ce soit.',
            scope: 'internal',
          },
          {
            text: 'Dans cette même matrice, « Tableaux kanban » se dit « Projets », comme partout ailleurs depuis la refonte.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.35',
    date: '2026-09-17',
    title: 'Suivi des appels : ce qui a mis fin à l\'attente d\'un correspondant',
    extranet: 'Suivi de vos appels : le sort de chaque appel manqué',
    features: [
      {
        title: 'Suivi des appels',
        items: [
          {
            text: '« À rappeler » explique désormais pourquoi un correspondant n\'y figure plus : une fenêtre liste, pour chacun, ses appels restés sans réponse et l\'appel abouti qui a mis fin à l\'attente (qui a rappelé qui, quand, combien de temps et depuis quel poste). Un appel décroché trop bref pour valoir un échange est signalé comme tel.',
            extranet: 'Vous pouvez désormais savoir ce qu\'il est advenu de chaque appel manqué : la fenêtre « Pourquoi cette liste ? » montre, en regard de chaque correspondant, l\'appel qui a mis fin à l\'attente, à votre initiative ou à la sienne, depuis n\'importe laquelle de vos lignes.',
          },
          {
            text: 'Le détail appel par appel s\'ouvre dans une fenêtre, qui garde la période et les filtres de l\'écran et porte les siens : l\'issue, le numéro appelant, le numéro appelé.',
            extranet: 'Le détail de vos appels s\'ouvre dans une fenêtre dédiée, qui reprend la période et les filtres affichés. Vous pouvez y rechercher un numéro appelant ou appelé sans que vos statistiques en soient modifiées.',
          },
        ],
      },
      {
        text: 'Signalement : une capture d\'écran peut accompagner un signalement.',
        scope: 'internal',
      },
    ],
    fixes: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          'Le fil de discussion se lit dans le sens où il s\'écrit.',
          'Le résumé d\'un projet dit l\'avancement plutôt que la répartition par colonne.',
          'La couleur d\'une équipe tient le bord du bandeau au lieu de l\'envahir.',
        ],
      },
    ],
  },
  {
    version: '2.48.34',
    date: '2026-09-16',
    title: 'Projets et Processus : les écrans repris d\'un bout à l\'autre',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'Une tâche se dessine désormais de la même façon partout : dans « Mes tâches », dans la vue Liste d\'un projet et sur le kanban. Les intervenants ont chacun leur couleur, dérivée de leur nom, et se chevauchent au lieu d\'occuper une colonne. Seul ce qui existe est affiché : une tâche sans échéance, sans échange et sans sous-tâche ne porte qu\'un titre, et cette sobriété fait ressortir ce qui déborde.',
            scope: 'internal',
          },
          {
            text: 'Le kanban n\'est plus l\'écran des tâches mais l\'une de ses vues : Kanban, Liste et Échéancier sont à égalité. Sur un projet de quarante tâches sans échéance, la liste sert mieux que les colonnes. Terminer une tâche depuis n\'importe laquelle des trois passe par le même geste qu\'au kanban.',
            scope: 'internal',
          },
          {
            text: 'Cliquer une tâche depuis « Mes tâches » n\'ouvrait rien lorsqu\'il s\'agissait d\'une tâche personnelle. Et le compte des sous-tâches faites n\'était jamais chargé : trois sur cinq s\'affichaient « 0 sur 5 ».',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'La liste des dossiers se lit enfin par ce sur quoi ils portent : le lien fibre, la ligne mobile, le client. Le processus devient le bandeau qui groupe, et compte les dossiers bloqués : savoir qu\'un raccordement était bloqué demandait auparavant d\'ouvrir chaque fiche.',
            scope: 'internal',
          },
          {
            text: 'Les étapes d\'un dossier forment un fil, avec un nœud par étape : plein quand elle est réglée, rouge quand elle est bloquée, creux tant que ce n\'est pas son tour. Une dérogation motivée et un blocage réel ont chacun leur traitement, au lieu d\'être du texte gris parmi d\'autres.',
            scope: 'internal',
          },
          {
            text: 'Le catalogue des modèles montre les étapes en chaîne, dans l\'ordre : on voit le chemin, sa longueur, et ce qui peut être sauté.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.33',
    date: '2026-09-16',
    title: 'Suivi des appels unifié pour l\'espace client, ventilation guidée des factures d\'achat',
    extranet: 'Suivi de vos appels : statistiques et détail réunis',
    features: [
      {
        title: 'Suivi des appels',
        items: [
          {
            text: 'L\'espace client affiche le détail appel par appel sur la page même, avec la répartition par issue et l\'export : le bouton « Voir le détail des appels » rouvrait un écran identique à celui qu\'on venait de quitter, et il fallait y reposer la période.',
            extranet: 'Le suivi de vos appels réunit désormais les statistiques et la liste détaillée sur la même page. La répartition par issue (répondus, sans réponse, occupés, abandonnés) s\'affiche d\'un coup d\'œil, et chaque ligne peut être exportée au format CSV.',
          },
          {
            text: 'Plusieurs filtres se combinent (une file, une issue, un poste) et chacun s\'enlève d\'un clic depuis la ligne qui les récapitule.',
            extranet: 'Vous pouvez croiser plusieurs filtres (une file, une issue, un poste) et retirer celui de votre choix sans perdre les autres.',
          },
          {
            text: 'Les graduations du graphique d\'évolution gardent leur taille quelle que soit la largeur de l\'écran.',
            extranet: 'Le graphique d\'évolution des appels s\'affiche correctement sur les grands écrans.',
          },
        ],
      },
      {
        title: 'Factures d\'achat',
        scope: 'internal',
        items: [
          'Le compte de charge d\'une ligne se choisit dans un sélecteur cherchable par numéro et par libellé, réservé aux lignes de charge : les écritures de TVA et de tiers restent calculées par le serveur.',
          'Chaque ligne indique d\'où vient le compte proposé : fiche fournisseur, déjà ventilé ainsi, saisie, enregistrement, ou à imputer quand rien ne permettait de le deviner.',
          'Un geste recopie le compte choisi sur toutes les lignes de charge de la facture.',
          'Une ligne de charge sans compte est signalée en haut de l\'écran, qui bloque l\'enregistrement au lieu de laisser croire que la ventilation est prête.',
          'La fiche fournisseur porte un compte de charge par défaut, réservé aux profils comptables.',
        ],
      },
      {
        text: 'Mes tâches : un champ en haut de l\'écran note une tâche pour soi en un geste ; elle n\'est affectée à aucun projet d\'équipe et reste visible de vous seul.',
        scope: 'internal',
      },
    ],
    fixes: [
      {
        text: 'Mes tâches : chaque intervenant sur une tâche a désormais sa propre couleur de pastille : elles étaient toutes bleues, indiscernables sans les survoler une à une.',
        scope: 'internal',
      },
      {
        text: 'Mes tâches : cliquer une tâche notée pour soi n\'ouvrait rien.',
        scope: 'internal',
      },
      {
        text: 'Suivi des appels : une page suivante demandée pendant le rechargement de la liste se raccrochait à la liste qu\'on venait de quitter, et les correspondants à rappeler pouvaient être annoncés absents sans avoir jamais été lus. Corrigé avant toute mise en service : aucun utilisateur n\'a pu le rencontrer.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.32',
    date: '2026-09-16',
    title: 'Fiche compte : le compte auxiliaire fournisseur d\'un groupe',
    features: [],
    fixes: [
      {
        text: 'Sur un site rattaché à un groupe, la fiche annonçait « Non saisi » pour le compte auxiliaire fournisseur alors que le code existe au niveau du groupe, où il se saisit. Elle affiche désormais le code qui s\'applique, en précisant qu\'il est défini sur la fiche principale.',
        scope: 'internal',
      },
      {
        text: 'Enregistrer la fiche entreprise d\'un site rattaché ne se solde plus par un refus pour les profils comptables, quand ce site porte encore un code auxiliaire hérité de l\'ancien rattachement multisite.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.31',
    date: '2026-09-16',
    title: 'Espace client : une permission retirée du catalogue',
    features: [],
    improvements: [],
    fixes: [
      {
        text: 'La permission qui autorisait un client à modifier ses coordonnées depuis son espace disparaît du catalogue : la saisie y est fermée, les comptes étant créés et tenus par Prosoluce. Aucun écran ne l\'exigeait plus.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.30',
    date: '2026-09-16',
    title: 'Le compteur de notifications laisse voir la cloche',
    features: [],
    improvements: [],
    fixes: [
      {
        text: 'Ramené dans la barre du haut, le compteur de notifications masquait la moitié du dessin de la cloche : il se cale désormais sur son coin, icône lisible.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.29',
    date: '2026-09-16',
    title: 'Notifications : la liste complète sans quitter l\'écran, et deux icônes qu\'on ne confond plus',
    features: [
      {
        text: 'La cloche ouvre la liste complète des notifications dans une fenêtre : lues, non lues, filtrables par type et par période, sans quitter l\'écran en cours. La page dédiée reste accessible depuis cette fenêtre.',
        scope: 'internal',
      },
      {
        text: 'Un projet s\'ouvre sur son résumé, le tableau de suivi devient un onglet.',
        scope: 'internal',
      },
    ],
    improvements: [
      {
        text: 'Fiche compte : « Notes » et « Documents » ne portent plus la même icône : les deux entrées étaient impossibles à distinguer d\'un coup d\'œil.',
        scope: 'internal',
      },
    ],
    fixes: [
      {
        text: 'Le compteur de la cloche ne déborde plus de la barre du haut, où il se faisait rogner.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.28',
    date: '2026-09-16',
    title: 'Fiche compte : le rôle se lit d\'emblée, les comptes auxiliaires le suivent',
    features: [],
    improvements: [
      {
        text: 'Le type de compte (client, fournisseur, ou les deux) passe en tête de la fiche, au-dessus de la forme juridique.',
        scope: 'internal',
      },
      {
        text: 'Les écrans de projets parlent désormais de projets et de tâches, là où ils disaient « tableaux » et « cartes ». L\'entrée du menu qui les liste tous s\'appelle « Tous les projets ».',
        scope: 'internal',
      },
    ],
    fixes: [
      {
        text: 'Le compte auxiliaire client (411) ne s\'affiche plus sur un compte qui n\'est pas client : le code restait visible après le retrait du rôle. Le compte auxiliaire fournisseur (401) obéit à la même règle, et un compte à double rôle montre les deux.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.27',
    date: '2026-09-15',
    title: 'Factures d\'achat : la réception à la demande, le justificatif et la TVA au détail',
    extranet: 'Mise à jour de vos coordonnées',
    features: [
      {
        title: 'Factures d\'achat',
        scope: 'internal',
        items: [
          'Un bouton « Vérifier maintenant » va chercher les factures chez la plateforme sans attendre le passage automatique, qui a lieu toutes les cinq minutes, et dit ce qui vient d\'arriver.',
          'Le justificatif d\'une facture se télécharge, qu\'il soit déjà classé dans la GED ou tout juste reçu.',
          'La TVA se lit taux par taux, avec le régime de chaque assiette, y compris l\'autoliquidation, qui n\'était pas signalée.',
          'Chaque pièce porte une référence interne, la nôtre, distincte du numéro du fournisseur et unique par exercice. Elle s\'édite sur la fiche, se cherche, et s\'affiche en colonne.',
          'L\'exercice comptable s\'affiche sur chaque facture et filtre la liste.',
          'Le reçu brut transmis par la plateforme se consulte, réservé aux super administrateurs.',
        ],
      },
    ],
    fixes: [
      {
        text: 'La saisie des coordonnées de contact depuis l\'espace client est fermée : leur mise à jour passe par les équipes internes.',
        extranet: 'La mise à jour de vos coordonnées passe désormais par votre interlocuteur Prosoluce.',
      },
      {
        text: 'Le téléchargement du justificatif d\'une facture d\'achat affiche l\'erreur renvoyée par le serveur, au lieu d\'un message générique.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.26',
    date: '2026-09-14',
    title: 'Comptes SIP qui s\'ouvrent, tableaux qui se distinguent',
    features: [],
    improvements: [
      {
        text: 'Projets : un tableau se reconnaît désormais du premier coup d\'œil : sa couleur se déduit de son nom quand aucune n\'a été choisie, au lieu de les afficher tous dans la même teinte, et un écusson de deux initiales le précède. Une ligne n\'affiche plus que ce qu\'elle a à dire : les compteurs vides et la jauge de progression disparaissent tant que rien n\'a bougé.',
        scope: 'internal',
      },
      {
        text: 'Mes tâches : la liste adopte la même langue : les échéances sont regroupées par fenêtre, chacune sous la couleur de ce qu\'elle annonce, et chaque ligne porte l\'écusson de son tableau, ou la marque de son processus lorsqu\'il s\'agit d\'une étape.',
        scope: 'internal',
      },
    ],
    fixes: [
      {
        text: 'Comptes SIP : l\'onglet ne s\'ouvrait pas du tout lorsque le chargement de la liste échouait : le clic restait sans effet et l\'écran précédent demeurait à l\'affichage, sans un mot. L\'onglet s\'ouvre maintenant dans tous les cas, et l\'échec s\'affiche.',
        scope: 'internal',
      },
      {
        text: 'Comptes SIP : le rafraîchissement automatique ne se superpose plus au chargement en cours. Une recherche, un changement de page ou une suppression demandés pendant ce rafraîchissement ne sont plus perdus : ils sont rejoués ensuite.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.25',
    date: '2026-09-11',
    title: 'Projets : une liste faite pour cent tableaux',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'La liste des tableaux est refaite pour la centaine qu\'elle contient réellement. Une ligne par tableau au lieu d\'une fiche : trois fois plus de tableaux à l\'écran, et les chiffres tombent dans la même colonne, si bien que la charge d\'une équipe se lit d\'un coup d\'œil.',
            scope: 'internal',
          },
          {
            text: 'En tête, « Mes cartes par tableau » rassemble les tableaux où des cartes vous sont assignées, ce qui déborde d\'abord. La section se remplit seule : rien à épingler, rien à régler.',
            scope: 'internal',
          },
          {
            text: 'Les équipes se replient d\'un clic, le pli étant retenu d\'une visite à l\'autre, et un champ de filtre cherche dans le nom, la description et l\'équipe au fil de la frappe.',
            scope: 'internal',
          },
          {
            text: 'Chaque tableau porte une couleur qui ne change plus : la sienne si l\'équipe en a choisi une, sinon une couleur dérivée de son nom. On la retrouve à l\'identique dans « Mes tâches », sur les cartes venues de ce tableau.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Mes tâches',
        scope: 'internal',
        items: [
          {
            text: 'L\'écran est resserré : les quatre fenêtres d\'échéance passent au premier plan, la priorité se lit sur la couleur du titre au lieu d\'une pastille de plus, et le tableau et la priorité rejoignent un bouton « Filtres » qui dit combien sont posés.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Services',
        scope: 'internal',
        items: [
          {
            text: 'La liste des services d\'un compte montre désormais les services suspendus autant que ceux en service. Ils n\'apparaissaient nulle part, et se faisaient recréer faute d\'être vus. Le libellé du filtre est dérivé de la liste affichée, pour qu\'il ne puisse plus mentir sur ce qu\'on regarde.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.24',
    date: '2026-09-11',
    title: 'Plans d\'appel : un bloc ajouté ne prend plus la place d\'un autre',
    features: [],
    fixes: [
      {
        text: 'Plans d\'appel : ajouter un bloc dans un plan déjà retouché en mode visuel n\'en fait plus disparaître un autre. Les blocs qu\'une édition passée avait rendus invisibles réapparaissent à l\'ouverture, sans leurs liaisons, avec un message qui invite à les rebrancher ou à les supprimer.',
        scope: 'internal',
      },
      {
        text: 'Plans d\'appel : un bloc posé dans l\'éditeur sans être relié à rien ne peut plus devenir le point d\'entrée du plan à la place du vrai.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.23',
    date: '2026-09-11',
    title: 'Mes tâches : une seule liste de ce qui m\'attend',
    features: [
      {
        title: 'Mes tâches',
        scope: 'internal',
        items: [
          {
            text: 'L\'écran ne montre plus seulement les cartes de vos tableaux : les étapes de processus qu\'on vous a confiées y figurent dans la même liste, mêlées aux cartes. Une étape se reconnaît à sa marque, et sa provenance dit le processus puis ce sur quoi porte le dossier : le service client concerné, le compte, ou « interne ». Un clic ouvre le dossier, descendu sur l\'étape. Elle se termine aussi d\'un clic depuis la liste, comme une carte.',
            scope: 'internal',
          },
          {
            text: 'Les étapes dont le tour n\'est pas venu n\'apparaissent pas : une liste de travail ne se remplit pas de choses qu\'on ne peut pas faire, et elles arriveront d\'elles-mêmes. Une étape signalée bloquée reste en revanche visible, car elle appelle une action.',
            scope: 'internal',
          },
          {
            text: 'Le classement « par projet » devient « par provenance », puisqu\'il regroupe maintenant aussi par processus. Le filtre par tableau et le filtre par priorité ne concernent que les cartes : les poser écarte les étapes, qui n\'ont ni l\'un ni l\'autre.',
            scope: 'internal',
          },
          {
            text: 'Le rangement à la main mêle les deux natures : on intercale une étape entre deux cartes. Il demande en revanche une liste entière, l\'ordre donné remplaçant le rangement complet : tant qu\'un filtre est posé, la prise disparaît et l\'écran dit pourquoi. Un glisser relâché en dehors de la liste remet tout comme avant.',
            scope: 'internal',
          },
          {
            text: 'L\'écran et son encart du tableau de bord s\'ouvrent à qui ne suit que des processus, sans droit sur les tableaux : c\'est là que ses étapes l\'attendent. La liste se parcourt aussi au clavier.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Une étape se confie à quelqu\'un depuis le dossier, et se reprend. Un dossier clos par erreur se rouvre : la clôture étant automatique, une case cochée par mégarde le fermait sans recours. Un dossier annulé ne se modifie plus, au même titre qu\'un dossier terminé.',
            scope: 'internal',
          },
          {
            text: 'Les actions qui n\'apparaissaient qu\'au survol sont visibles sur les écrans tactiles, où le survol n\'existe pas. Un bouton grisé et un champ verrouillé disent pourquoi ils le sont, et une recherche qui échoue le signale au lieu de rendre une liste vide.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.22',
    date: '2026-09-10',
    title: 'Processus : choisir un service par son nom, et six guides d\'aide',
    features: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'À l\'ouverture d\'un dossier, on cherche le client par son nom puis on choisit parmi ses services. Seuls ceux du type attendu par le processus sont proposés. Les types de service se choisissent eux aussi dans une liste, avec le nombre de services actifs de chacun. Plus aucun identifiant à saisir de mémoire.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Documentation',
        scope: 'internal',
        items: [
          {
            text: 'Six guides pour la gestion de projet et les processus de service : le principe de chaque module, « Mes tâches », la discussion et les notifications, la définition d\'un modèle, le suivi d\'un dossier.',
            scope: 'internal',
          },
          {
            text: 'Un sélecteur de catégorie ramène le sommaire à un domaine, et se combine avec la recherche.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.21',
    date: '2026-09-10',
    title: 'Processus de service : catalogue et suivi des dossiers',
    features: [
      {
        title: 'Processus',
        scope: 'internal',
        items: [
          {
            text: 'Un menu « Processus » réunit les dossiers en cours et, pour qui administre, le catalogue des modèles. Un modèle décrit un enchaînement qu\'on refait à l\'identique (un raccordement, une mise en service, une résiliation), avec ses étapes ordonnées et celles qui peuvent être sautées.',
            scope: 'internal',
          },
          {
            text: 'Un dossier s\'ouvre sur un service souscrit, sur un compte, ou sur rien du tout pour ce qui est interne : un recrutement, un audit. Il copie les étapes du modèle à son ouverture, si bien que remanier un modèle ne touche aucun dossier en cours.',
            scope: 'internal',
          },
          {
            text: 'Quand le processus impose l\'ordre, une étape qui n\'est pas encore la sienne indique celle qui la retient. La terminer quand même reste possible en expliquant pourquoi, et l\'explication reste attachée au dossier. Une étape peut aussi être signalée bloquée, par exemple pour un document manquant ou un client qui ne répond pas. Le dossier se clôt de lui-même quand toutes ses étapes sont réglées.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.20',
    date: '2026-09-10',
    title: 'Grilles tarifaires : tarifer un forfait, et des boutons qui disent vrai',
    features: [
      {
        title: 'Grilles tarifaires',
        scope: 'internal',
        items: [
          {
            text: 'L\'écran d\'un forfait indique dans quelles grilles il est tarifé, à l\'achat comme à la vente, et permet d\'y poser son prix sans avoir à refabriquer une grille entière autour de lui. Une grille qui a servi à facturer s\'affiche en lecture seule : son prix ne se corrige plus, mais un produit qui n\'y figure pas encore peut toujours y être ajouté.',
            scope: 'internal',
          },
          {
            text: 'Le contenu d\'un forfait et son enveloppe (ce qu\'il décompte et ce qui se passe au-delà) se règlent depuis son écran. Une composition qui reviendrait sur elle-même est refusée en nommant le forfait fautif.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Notifications',
        scope: 'internal',
        items: [
          {
            text: 'Un historique rassemble tout ce qui vous a été notifié, y compris ce que la cloche n\'affiche plus, avec un filtre par état, par type et par période. Il s\'ouvre depuis la cloche.',
            scope: 'internal',
          },
          {
            text: 'Une notification ouverte sans être traitée peut retourner dans la pile : un bouton la remet en non lue, et la cloche la compte de nouveau.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        text: 'Grilles tarifaires : les boutons de suppression annonçaient une suppression réussie sans avoir rien supprimé. Un produit ou un service part désormais pour de bon, ou l\'écran indique ce qui le retient : combien de grilles le tarifent, combien de forfaits le citent, combien de lignes y sont abonnées.',
        scope: 'internal',
      },
      {
        text: 'Grilles tarifaires : chercher un produit par son nom ne déborde plus du service sélectionné.',
        scope: 'internal',
      },
      {
        text: 'Notifications : le contenu d\'une notification ne se coupe plus au milieu d\'un mot, et « tout marquer comme lu » ne promet plus ce qu\'il ne tient pas.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.19',
    date: '2026-09-10',
    title: 'Projets : classement des tâches, liens entre cartes, tableaux archivables',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'La liste « Mes tâches » se classe comme vous voulez : par échéance, priorité, projet, ordre d\'arrivée, titre, ou à la main. Le regroupement suit le tri choisi, et « mon ordre » permet de ranger vos tâches par glisser-déposer. Ce rangement est le vôtre : il ne change rien pour les autres, ni la place des cartes dans leurs colonnes.',
            scope: 'internal',
          },
          {
            text: 'Un tableau se renomme, s\'archive et se supprime, depuis la liste comme depuis le tableau ouvert. Archiver ne détruit rien : le tableau reste consultable et sort simplement de la liste des tableaux actifs. La suppression, elle, emporte les cartes et leurs échanges, et demande de retaper le nom du tableau.',
            scope: 'internal',
          },
          {
            text: 'La fiche d\'un tableau montre la charge plutôt qu\'un pourcentage : une case par carte, pleine pour ce qui est fait, creuse pour ce qui reste, rouge pour ce qui déborde. L\'échéance s\'exprime en délai (« à rendre dans six jours ») plutôt qu\'en date à recalculer.',
            scope: 'internal',
          },
          {
            text: 'Deux cartes peuvent être reliées, y compris d\'un tableau à l\'autre : tapez quelques lettres du titre, choisissez, c\'est fait. Le lien se précise ensuite d\'un clic : « bloque », « doublon de », « en rapport avec ». Il n\'empêche rien : une carte signalée comme bloquée se déplace et se modifie comme les autres.',
            scope: 'internal',
          },
          {
            text: 'Le panneau d\'une carte indique de quel tableau elle relève, à quelle étape elle se trouve et quelle équipe la porte, ce qui est utile lorsqu\'elle est ouverte depuis « Mes tâches », depuis une notification ou depuis la discussion.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Grilles tarifaires',
        scope: 'internal',
        items: [
          {
            text: 'Un forfait mobile se crée et se configure depuis l\'application : ce qu\'il contient, ce qu\'il décompte.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        text: 'Comptes : toutes les sections de la fiche client sont de nouveau atteignables par leur adresse.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.18',
    date: '2026-09-09',
    title: 'Notifications : la cloche vérifie trois fois par minute',
    features: [
      {
        title: 'Notifications',
        scope: 'internal',
        items: [
          {
            text: 'La cloche interroge le serveur toutes les vingt secondes au lieu d\'une minute : une carte qui vous est confiée ou un message qui vous cite se voit sans avoir à recharger la page. Le rythme s\'interrompt quand l\'onglet passe en arrière-plan et reprend au retour.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.17',
    date: '2026-09-09',
    title: 'Projets : mes tâches, mode d\'emploi et discussion du tableau',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'Un écran « Mes tâches » réunit les cartes qui vous sont assignées, tous tableaux confondus, groupées par échéance : en retard, aujourd\'hui, cette semaine, sans échéance. Terminer une carte depuis cet écran la dépose dans l\'étape finale de son tableau, avec le même suivi et les mêmes notifications qu\'au kanban.',
            scope: 'internal',
          },
          {
            text: 'Le tableau de bord porte un encart de vos cinq tâches prioritaires ; il reste invisible quand il n\'y a rien à rappeler.',
            scope: 'internal',
          },
          {
            text: 'Le mode d\'emploi d\'un tableau, jusque-là saisi sans être visible nulle part, s\'affiche au-dessus des colonnes dans un bandeau qui se replie et retient son état d\'un tableau à l\'autre. Il se corrige sur place.',
            scope: 'internal',
          },
          {
            text: 'Une discussion générale s\'ouvre depuis la barre du tableau ; elle réunit le fil du tableau, les commentaires de ses cartes (chacun renvoyant à la sienne d\'un clic) et son activité : carte créée, terminée, rouverte, confiée à quelqu\'un ou supprimée.',
            scope: 'internal',
          },
          {
            text: 'La pastille de cette discussion annonce ce que vous n\'avez pas lu, vos propres messages exclus, et non le nombre total de messages. Ouvrir le panneau la remet à zéro, et un trait « vu jusqu\'ici » marque dans le fil l\'endroit où vous vous étiez arrêté.',
            scope: 'internal',
          },
          {
            text: 'Le glisser-déposer d\'une carte se voit mieux : la prise apparaît au survol, la colonne visée se signale pendant le geste, et l\'en-tête de chaque colonne garde la couleur de son étape en restant visible quand une longue liste défile.',
            scope: 'internal',
          },
          {
            text: 'Un lien reçu par notification ouvre directement le tableau avec la carte concernée dépliée.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          {
            text: 'Avant d\'enregistrer une adresse de facturation ou des informations d\'entreprise portées par un contact que plusieurs comptes partagent, l\'écran demande quoi faire : appliquer le changement à tous les comptes concernés, qui sont nommés, ou n\'agir que sur celui-ci. La question n\'est pas posée quand l\'enregistrement ne touche aucun autre compte.',
            scope: 'internal',
          },
        ],
      },
    ],
    fixes: [
      {
        text: 'Luce : la réponse s\'affiche au fil de sa rédaction, à un débit régulier, au lieu d\'apparaître d\'un bloc une fois la génération terminée. Le fil ne redescend plus de force quand on est remonté y relire quelque chose, et le bouton de téléchargement d\'une réponse apparaît sans attendre la réouverture de la conversation.',
        scope: 'internal',
      },
    ],
  },
  {
    version: '2.48.16',
    date: '2026-09-08',
    title: 'Documents : un fichier peut peser jusqu\'à 50 Mo',
    features: [
      {
        title: 'Documents',
        scope: 'internal',
        items: [
          {
            text: 'Le plafond de dépôt d\'un document passe de 20 à 50 Mo. L\'écran annonce désormais la limite que le serveur applique réellement, au lieu d\'en écrire une de son côté : le message d\'aide et le refus avant envoi ne peuvent plus diverger de ce qui est vraiment accepté.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.15',
    date: '2026-09-07',
    title: 'Audit de la gestion de projet dans l\'Administration',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'Le journal d\'audit de la gestion de projet rejoint le bloc Administration, aux côtés des autres journaux, et n\'apparaît plus dans le menu Projets.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.14',
    date: '2026-09-07',
    title: 'Gestion de projet : correction d\'affichage',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'L\'écran des projets s\'ouvre de nouveau : il lisait les réponses du serveur un niveau trop bas et restait bloqué sur une erreur.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.13',
    date: '2026-09-07',
    title: 'Gestion de projet par équipe',
    features: [
      {
        title: 'Projets',
        scope: 'internal',
        items: [
          {
            text: 'Un module de gestion de projet fait son entrée : des tableaux par équipe, en colonnes, où l\'on déplace les cartes à la souris. Une carte porte ses responsables, ses étiquettes, son échéance, sa liste de sous-tâches et sa discussion.',
            scope: 'internal',
          },
          {
            text: 'Les équipes se composent à part des rôles : un rôle dit ce qu\'une personne a le droit de faire, une équipe dit avec qui elle travaille. Un chef d\'équipe compose la sienne sans avoir besoin des droits d\'administration.',
            scope: 'internal',
          },
          {
            text: 'Tout ce qui se passe sur un tableau est consigné dans un journal consultable et exportable, qui reste lisible même après le départ d\'un collaborateur.',
            scope: 'internal',
          },
          {
            text: 'Les personnes assignées à une carte sont prévenues à l\'approche de l\'échéance, une fois par échéance. L\'ancien écran de tâches, qui n\'était atteignable par aucun menu et dont les rappels ne partaient jamais, est retiré.',
            scope: 'internal',
          },
          {
            text: 'Les droits du module ne sont attribués à personne au départ : il faut les donner depuis la matrice des droits pour que les tableaux apparaissent.',
            scope: 'internal',
          },
        ],
      },
      {
        title: 'Signalement d\'un problème',
        scope: 'internal',
        items: [
          {
            text: 'Un signalement indique désormais au tri automatique l\'écran d\'où il part : le chemin, le nom technique de l\'écran et l\'intitulé du menu sont transmis à part, au lieu de figurer seulement dans le bloc de contexte ajouté à la description. Ce bloc était retiré avant lecture, et le tri perdait avec lui la seule indication fiable sur l\'endroit concerné, qu\'un texte d\'utilisateur ne donne presque jamais.',
            scope: 'internal',
          },
          {
            text: 'L\'intitulé du menu apparaît en plus à côté du chemin dans le ticket créé.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.12',
    date: '2026-09-07',
    title: 'Factures d\'achat : le PDF préremplit la saisie',
    features: [
      {
        title: 'Factures d\'achat',
        scope: 'internal',
        items: [
          {
            text: 'Le PDF d\'une facture fournisseur peut être lu pour préremplir la saisie : fournisseur, numéro, dates, lignes et montants sont proposés à l\'écran, puis corrigés et validés à la main. Rien n\'est enregistré sans cette validation.',
            scope: 'internal',
          },
          {
            text: 'Ce qui est proposé est recontrôlé avant d\'être affiché : les totaux sont recalculés, la clé du SIRET et celle du numéro de TVA vérifiées, le code TVA résolu depuis le paramétrage comptable. Ce qui ne se recoupe pas est signalé plutôt que corrigé en silence. Le choix du compte de charge reste au comptable et n\'est jamais proposé.',
            scope: 'internal',
          },
          {
            text: 'Un document numérisé, qui ne porte aucun texte à lire, est refusé avec un message clair plutôt que de rendre une proposition devinée.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.11',
    date: '2026-09-04',
    title: 'Plans d\'appel : destinations conservées à la renumérotation',
    features: [
      {
        title: 'Plans d\'appel',
        scope: 'internal',
        items: [
          {
            text: 'Enregistrer un plan d\'appel renumérote ses actions. Les blocs de condition calendaire (jour férié, mode jour/nuit, semaine paire/impaire, vérification de compte) n\'y suivaient pas : leurs destinations restaient sur les anciens numéros et l\'appel repartait au mauvais endroit.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.9',
    date: '2026-08-19',
    title: 'Fiches et tableaux de bord qui s\'ouvraient vides',
    extranet: 'Votre espace de nouveau complet',
    features: [
      {
        title: 'Comptes',
        extranet: 'Votre espace',
        items: [
          {
            text: 'La fiche d\'un compte s\'ouvrait sans la moindre information (forme juridique, raison sociale, SIRET, adresse) et ses onglets de configuration n\'apparaissaient plus.',
            extranet: 'Votre espace s\'ouvrait sans vos informations, et une partie de vos onglets manquait à l\'appel.',
          },
          {
            text: 'Même effet sur la remise de prélèvements, le comparatif de CA et la gestion des numéros fixes.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.8',
    date: '2026-08-18',
    title: 'Choix du client dans les mouvements de stock',
    features: [
      {
        title: 'Stock',
        scope: 'internal',
        items: [
          'Le choix du client, lors d\'un mouvement de stock, s\'étend à tous les comptes. Seuls les premiers étaient proposés, et la recherche ne portait que sur eux : les autres restaient introuvables.',
          'La recherche interroge désormais le serveur à la frappe, et remonte aussi les sous-comptes.',
          'Même correction sur le scan de sortie, dont la recherche de client restait sans effet.',
        ],
      },
    ],
  },
  {
    version: '2.48.7',
    date: '2026-08-18',
    title: 'Filtrage avancé des comptes',
    features: [
      {
        title: 'Filtrage avancé',
        scope: 'internal',
        items: [
          'Les listes « Chargé de compte » et « Services souscrits » se remplissent pour les chargés de compte.',
          'Les deux référentiels de l\'écran se chargent indépendamment. Ils relèvent de droits distincts, et le refus du premier laissait le second sans être appelé : la liste des services paraissait vide alors que rien ne l\'avait demandée.',
          'Une liste vide distingue désormais l\'absence de donnée du refus d\'accès.',
        ],
      },
    ],
  },
  {
    version: '2.48.6',
    date: '2026-08-18',
    title: 'Stock au millième, zones de couverture et documentation',
    extranet: 'Documentation : version et date de chaque guide',
    features: [
      {
        title: 'Stock',
        scope: 'internal',
        items: [
          'Les prix se saisissent et s\'affichent au millième d\'euro. Un produit vendu au mètre se négocie à 0,995 €, et l\'arrondi au centime faussait la valeur de chaque bobine, puis celle du stock entier.',
          'Le champ de prix accepte désormais le millième, là où le pas de saisie imposait l\'unité. Une saisie plus fine que le millième est refusée plutôt qu\'arrondie sans le dire.',
          'Les quantités restent entières : une bobine se compte en mètres entiers, seul son prix porte des millièmes.',
        ],
      },
      {
        title: 'Carte des comptes',
        scope: 'internal',
        items: [
          'Les chargés de compte voient les zones de couverture des fournisseurs. Leur consultation relevait du droit d\'administration réseau, qui emporte la modification des fournisseurs, des VLANs et des adresses IP : elle a désormais le sien, en lecture seule.',
          'Le sélecteur de couverture ne s\'affiche plus qu\'à qui peut s\'en servir, au lieu de rester vide.',
        ],
      },
      {
        title: 'Documentation',
        items: [
          {
            text: 'Chaque guide porte sa version et sa date de mise à jour sous la fiche, déduites de l\'historique du dépôt : rien à saisir en l\'écrivant, donc rien à oublier en la corrigeant.',
            extranet: 'Chaque guide indique quand il a été mis à jour pour la dernière fois.',
          },
        ],
      },
      {
        title: 'Navigation',
        scope: 'internal',
        items: [
          'Le renouvèlement quitte le menu Commercial pour Facturation & Compta.',
        ],
      },
    ],
  },
  {
    version: '2.48.5',
    date: '2026-08-18',
    title: 'Téléphonie : statistiques et paramétrage d\'une ligne',
    features: [
      {
        title: 'Téléphonie',
        scope: 'internal',
        items: [
          'Les chargés de compte accèdent aux statistiques de téléphonie fixe et mobile d\'une ligne.',
          'La roue crantée d\'une ligne mobile ne mène plus qu\'au paramétrage. La consommation et le détail des appels s\'ouvrent par le bouton qui leur est dédié, où ils figuraient déjà : deux portes menaient au même écran, chacune avec ses propres conditions d\'accès.',
        ],
      },
    ],
  },
  {
    version: '2.48.4',
    date: '2026-08-18',
    title: 'Stock : sortie de la fiche produit',
    features: [
      {
        title: 'Stock',
        scope: 'internal',
        items: [
          'Quitter la fiche d\'un produit ne déclenche plus un message d\'erreur : l\'écran redemandait la fiche au moment même où il la quittait, sans plus de référence à charger.',
        ],
      },
    ],
  },
  {
    version: '2.48.3',
    date: '2026-08-19',
    title: 'Analyse des appels : unités de la grille horaire',
    extranet: 'Grille horaire à la bonne unité',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "Les créneaux de sollicitation adoptent l'unité qui convient aux valeurs, comme la courbe. Une attente de quelques secondes affichait « 0 » dans chaque case, la grille ne disant plus rien de ce qu'elle colorait.",
            extranet: 'Les durées dans la grille horaire s\'affichent dans l\'unité qui convient.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.2',
    date: '2026-08-18',
    title: 'Notes et gestion des droits',
    extranet: 'Notes',
    features: [
      {
        title: 'Notes',
        scope: 'internal',
        items: [
          'La saisie ne propose que les catégories de notes que l\'on a le droit d\'écrire, et une note d\'une autre catégorie ne peut plus être modifiée ni supprimée, seulement lue.',
          'Les notes antérieures à février 2026 étant toutes classées en « général », en ouvrir la lecture à un rôle revenait auparavant à lui ouvrir la réécriture de tout l\'historique.',
        ],
      },
      {
        title: 'Gestion des droits',
        scope: 'internal',
        items: [
          'Le troisième palier d\'éligibilité apparaît dans l\'écran « Périmètre fournisseurs », où seuls deux des trois figuraient.',
        ],
      },
    ],
  },
  {
    version: '2.48.1',
    date: '2026-08-19',
    title: 'Analyse des appels : échelle des durées',
    extranet: 'Durées à la bonne unité',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "L'axe des durées choisit son unité selon les valeurs tracées : secondes pour une attente, minutes ou heures pour un temps de conversation. À la minute imposée, une attente de douze secondes affichait « 0 min » sur toute la hauteur.",
            extranet: 'Les durées sur les graphiques s\'affichent dans l\'unité qui convient : secondes, minutes ou heures selon les valeurs.',
          },
        ],
      },
    ],
  },
  {
    version: '2.48.0',
    date: '2026-08-18',
    title: 'Stock : un onglet pour ce qui se compte en quantité',
    features: [
      {
        title: 'Stock : inventaire',
        scope: 'internal',
        items: [
          "Un onglet Stock réunit les produits gérés en quantité, que l'inventaire laissait sans ligne faute d'exemplaires à leur nom alors qu'ils font l'essentiel du stock : quantité et unité, valeur, statut, et de quoi n'en garder qu'un régime",
          "L'onglet s'efface pour qui n'a pas le droit de parcourir le catalogue, d'où il tire ses données",
        ],
      },
      {
        title: 'Documentation',
        scope: 'internal',
        items: [
          {
            text: "Le catalogue des guides est lu sur une adresse qui lui est propre. L'ancienne était aussi celle de l'interface de documentation technique : selon l'ordre de démarrage du serveur, l'une écrasait l'autre et l'écran pouvait tomber en erreur sans rien avoir changé.",
          },
        ],
      },
    ],
  },
  {
    version: '2.47.3',
    date: '2026-08-18',
    title: 'Stock : l\'inventaire s\'ouvre sur tout le parc',
    features: [
      {
        title: 'Stock : inventaire',
        scope: 'internal',
        items: [
          'L\'inventaire s\'ouvre sur tout le parc, matériel piloté par le provisioning compris : le filtre démarre sur « Tout le parc » et non plus sur « Sans les managés »',
          'Les lignes pilotées par le provisioning restent en lecture : leurs actions et leurs adresses MAC se règlent depuis le provisioning, seul à les résoudre',
        ],
      },
    ],
  },
  {
    version: '2.47.2',
    date: '2026-08-19',
    title: 'Analyse des appels : couleurs et axe réellement appliqués',
    extranet: 'Graphiques corrigés',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "La courbe, son point de survol et le cadre des graphes prennent la couleur de la mesure choisie. Faute de couleur définie, la ligne se traçait en transparent, le point tombait au noir et le cadre restait gris.",
            extranet: 'La couleur de la mesure choisie s\'applique à la courbe et au cadre des graphiques.',
          },
          {
            text: "L'axe des durées se lit en heures et en minutes : l'unité n'était pas transmise au graphique, qui affichait donc toujours des secondes.",
            extranet: 'Les durées sur les graphiques s\'affichent en heures et minutes.',
          },
        ],
      },
    ],
  },
  {
    version: '2.47.1',
    date: '2026-08-19',
    title: 'Analyse des appels : couleurs et échelle des durées',
    extranet: 'Graphiques plus lisibles',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "La carte de mesure choisie donne sa couleur à la courbe et au contour des blocs qu'elle commande : on voit d'où vient ce qui a changé.",
            extranet: 'La mesure que vous sélectionnez colore la courbe et l\'encadré des graphiques correspondants.',
          },
          {
            text: "L'axe des durées se lit en heures et en minutes. Il affichait auparavant les secondes brutes (« 15000 », « 22500 »), difficiles à convertir de tête.",
            extranet: 'Les durées sur les graphiques s\'affichent en heures et minutes.',
          },
        ],
      },
    ],
  },
  {
    version: '2.47.0',
    date: '2026-08-17',
    title: 'Création d\'un compte : remplissage par le SIREN',
    features: [
      {
        title: 'Fiche compte',
        scope: 'internal',
        items: [
          "Le numéro SIREN ou SIRET saisi renseigne la raison sociale, l'adresse, le code postal, la ville et le code INSEE depuis l'annuaire des entreprises",
          "Un SIREN seul est complété par le SIRET de l'établissement",
          "Ce qui a déjà été saisi n'est remplacé qu'après confirmation : l'annuaire propose, vous décidez",
          "Les comptes portant déjà ce SIREN sont signalés avant d'en créer un doublon",
          "Un établissement fermé au répertoire est signalé, et une entreprise opposée à la diffusion de ses informations renvoie vers la saisie manuelle",
        ],
      },
    ],
  },
  {
    version: '2.46.0',
    date: '2026-08-17',
    title: 'Gestion de stock',
    features: [
      {
        title: 'Produits vendus au metrage',
        scope: 'internal',
        items: [
          'Un produit peut desormais se gerer au metrage ou au poids : chaque contenant porte sa quantite et se debite, si bien qu\'un touret de fibre entame reste disponible avec son reliquat',
          'Une sortie preleve une longueur sur le contenant designe, qui reste en stock avec ce qu\'il lui reste ; sans contenant designe, le systeme puise du plus ancien au plus recent',
          'Un contenant epuise sort seul du stock, et un retour lui rend la longueur non posee',
          'Le contenant se choisit d\'après ce qu\'il porte (« TOURET-4471 (760 m) ») sans avoir a ouvrir sa fiche',
          'L\'unite accompagne les chiffres a l\'ecran, et la liste des exemplaires affiche le reliquat de chaque contenant',
        ],
      },
      {
        title: 'Fiche produit',
        scope: 'internal',
        items: [
          'Une seule question, « comment ce produit se gere-t-il », remplace les reglages separes dont les combinaisons n\'etaient pas toutes possibles',
          'L\'unite et l\'identifiant n\'apparaissent que lorsqu\'ils ont un sens, et l\'aide sous le champ decrit le mode retenu',
        ],
      },
    ],
  },
  {
    version: '2.45.3',
    date: '2026-08-17',
    title: 'Analyse des appels : cartes visibles et écran réordonné',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "Les cartes de mesure s'affichent enfin : le composant n'était pas déclaré, la balise restait donc invisible sans qu'aucune erreur ne le signale.",
          "Le sens de l'appel passe au-dessus de la période et des filtres, qui ne font que restreindre ce qu'il montre.",
          "Le bouton de filtres annonce ce qu'il fait, et combien de filtres restent actifs quand le panneau est replié.",
          "Les filtres par numéro ne s'affichent que sur la liste et se vident en la quittant : ramenées à un seul correspondant, les statistiques ne décrivent plus rien.",
        ],
      },
    ],
  },
  {
    version: '2.45.2',
    date: '2026-08-17',
    title: 'Mode sombre : les bonnes couleurs, dès l\'ouverture',
    extranet: 'Le mode sombre affiche enfin les bonnes couleurs',
    features: [
      {
        title: 'Affichage',
        items: [
          {
            text: 'Le mode sombre était incomplet : les couleurs de survol, les champs désactivés, les ombres et les fonds de message restaient en clair. L\'ensemble suit désormais le thème.',
            extranet: 'En mode sombre, les couleurs de survol, les champs inactifs et les messages suivent désormais le thème, au lieu de rester en clair.',
          },
          {
            text: 'Les textes et les aplats bleu nuit étaient illisibles sur fond sombre, jusqu\'à se confondre avec leur propre fond dans les listes déroulantes et les onglets. Ils sont éclaircis, et le survol éclaircit au lieu d\'assombrir.',
            extranet: 'Les textes en bleu se lisent maintenant correctement sur fond sombre, y compris dans les listes déroulantes et les onglets.',
          },
          {
            text: 'La page ne s\'affiche plus en clair une fraction de seconde avant de basculer : le thème est posé avant le premier rendu.',
            extranet: 'La page ne clignote plus en clair au chargement avant de passer en sombre.',
          },
        ],
      },
    ],
  },
  {
    version: '2.45.1',
    date: '2026-08-17',
    title: 'Analyse des appels : une seule rangée de cartes',
    extranet: 'Vos mesures en cartes',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "Une seule rangée de cartes, toutes cliquables, au lieu de deux rangées dont une seulement l'était. La moyenne s'y compte par jour ouvré, les samedis et dimanches ne diluant plus le chiffre.",
            extranet: "Vos mesures sont regroupées en cartes : cliquez celle que vous voulez voir sur la courbe et la grille horaire.",
          },
        ],
      },
    ],
  },
  {
    version: '2.45.0',
    date: '2026-08-17',
    title: 'Analyse des appels : mesures et transitions',
    extranet: 'Choisissez ce que vous regardez',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "L'espace client reçoit lui aussi les cartes de mesure : cliquer une carte change la courbe et la grille horaire.",
            extranet: "Choisissez la mesure à afficher (appels, décrochés, temps de conversation, attente) et la courbe comme la grille horaire s'y adaptent.",
          },
        ],
      },
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "Le passage d'un onglet à l'autre se fait en fondu, sans mouvement pour qui a demandé à en limiter.",
        ],
      },
    ],
  },
  {
    version: '2.44.9',
    date: '2026-08-17',
    title: 'Analyse des appels : mesures en cartes',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "Le menu du haut réunit les trois sens de statistiques et la liste des appels ; la bascule statistiques / liste qui le doublait est retirée.",
          "Les quatre mesures sont présentées en cartes portant chacune sa moyenne sur la période. Cliquer une carte change la courbe et la grille horaire.",
          "La grille horaire passe en échelle chaude : l'intensité se lit comme une charge, et non comme une quantité parmi d'autres.",
        ],
      },
    ],
  },
  {
    version: '2.44.8',
    date: '2026-08-17',
    title: 'Analyse des appels : lecture des indicateurs',
    extranet: 'Vos statistiques expliquées',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "Chaque indicateur porte une icône d'aide qui dit ce qu'il mesure. Celle du niveau de service précise en quoi il diffère du taux de décrochage, les deux se rapprochant naturellement alors qu'ils ne comptent pas la même chose.",
            extranet: "Chaque chiffre de vos statistiques d'appels peut être expliqué : survolez l'icône d'aide à côté de son titre.",
          },
        ],
      },
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "Les créneaux de sollicitation affichent le nombre d'appels dans chaque case, et toutes les heures sont repérées au lieu d'une sur deux.",
          'La valeur et la part de chaque tranche des anneaux sont mises en évidence.',
        ],
      },
      {
        title: 'Recherche',
        scope: 'internal',
        items: [
          'Le champ de recherche se vide une fois le compte choisi, la recherche suivante part de zéro.',
        ],
      },
    ],
  },
  {
    version: '2.44.7',
    date: '2026-08-17',
    title: 'Votre consommation téléphonique',
    extranet: 'Votre consommation téléphonique',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: "La consommation facturée apparaît dans l'espace client, sous les appels émis, détaillée par destination.",
            extranet: "Votre consommation téléphonique apparaît sous vos appels émis, destination par destination, avec le montant hors taxes qui figurera sur votre facture.",
          },
        ],
      },
    ],
  },
  {
    version: '2.44.6',
    date: '2026-08-17',
    title: 'Analyse des appels : consommation facturée',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "La consommation facturée du client apparaît sous les appels émis : montant hors taxes, nombre d'appels et durée facturée, détaillés par destination.",
          "Les montants sont ceux de la taxation et ne sont pas recalculés ; l'écran indique jusqu'à quelle date ils sont complets.",
        ],
      },
    ],
  },
  {
    version: '2.44.5',
    date: '2026-08-17',
    title: 'Analyse des appels : lisibilité',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "Le temps cumulé débordait du cercle qui le porte ; la valeur se resserre quand elle est longue.",
          "Les colonnes de la grille horaire avaient des largeurs inégales alors qu'elles représentent des créneaux tous égaux.",
          "Le niveau de service annonce son calcul en toutes lettres : rapproché du taux de décrochage, qui compte les deux sens et ignore le délai, l'écart entre les deux passait pour une erreur.",
        ],
      },
    ],
  },
  {
    version: '2.44.4',
    date: '2026-08-17',
    title: 'Analyse des appels : liste séparée et onglets alignés',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "La liste des appels passe dans son propre onglet : l'atteindre demandait de faire défiler tout le tableau de bord.",
          'Les onglets de sens tiennent sur une ligne au lieu de se ranger en carré.',
          "La durée moyenne d'un appel s'affiche de nouveau ; elle restait vide sur la vue tous sens confondus, qui ne la portait pas.",
          "Le classement des postes explique l'écart entre le nombre d'appels d'un poste et ceux qu'il a réellement pris.",
        ],
      },
    ],
  },
  {
    version: '2.44.3',
    date: '2026-08-15',
    title: 'Analyse des appels : comptes par sens corrigés',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "Les compteurs des onglets annonçaient « 0 appel » pour les sens que l'on ne regardait pas : la synthèse était demandée filtrée sur l'onglet affiché, alors qu'elle porte déjà les trois sens.",
          "L'onglet « tous les appels » est retiré : mêler appels reçus et appels émis produit des moyennes qui ne décrivent rien.",
        ],
      },
    ],
  },
  {
    version: '2.44.2',
    date: '2026-08-14',
    title: 'Analyse des appels : même présentation en interne',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          "L'écran interne adopte la présentation du tableau de bord client : jauge de niveau de service, répartition des appels et du temps en ligne, tuiles de durée, courbe, créneaux de sollicitation et classements.",
          'Le sens de l\'appel passe en onglets (tous, reçus, émis, internes) et quitte le panneau de filtres, où il faisait doublon.',
          'La mesure tracée par la courbe se choisit dans un sélecteur ; le geste était porté par les cartes du haut, qu\'il fallait deviner cliquables.',
          'La répartition horaire se lit par jour de la semaine : un pic de 11 h ne disait pas s\'il valait pour tous les jours ou pour le seul mardi.',
        ],
      },
    ],
  },
  {
    version: '2.44.1',
    date: '2026-08-14',
    title: 'Tableau de bord téléphonie de l\'espace client',
    extranet: 'Votre téléphonie',
    features: [
      {
        title: 'Ma téléphonie',
        items: [
          {
            text: 'La téléphonie a son tableau de bord dans l\'espace client, avec une bascule entre appels reçus et appels émis : niveau de service, répartition des appels et du temps passé en ligne, durées moyennes, évolution jour après jour, créneaux de sollicitation, files d\'attente et activité de chaque poste.',
            extranet: 'Un nouvel écran vous montre vos appels reçus et vos appels émis séparément : combien sont pris, en combien de temps, à quelles heures vous êtes le plus sollicités, et l\'activité de chacun de vos postes.',
          },
          {
            text: 'La navigation de l\'espace client passe en colonne d\'icônes le long du contenu, au lieu d\'une barre horizontale qui disparaissait au défilement.',
            extranet: 'La navigation de votre espace reste visible en permanence, dans une colonne d\'icônes à gauche.',
          },
        ],
      },
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Un enregistrement vocal se dépose dans la conversation comme un document, avec son propre plafond de taille et une attente annoncée comme transcription.',
          'Un nouveau dépôt est refusé tant qu\'une transcription est en cours, pour ne pas joindre la réponse au mauvais message.',
        ],
      },
    ],
  },
  {
    version: '2.44.0',
    date: '2026-08-14',
    title: 'Documentation et gestion des droits',
    extranet: 'Documentation',
    features: [
      {
        title: 'Documentation',
        items: [
          {
            text: 'Quatorze nouveaux guides : comptes et contacts, dont les contacts partagés entre plusieurs comptes et les comptes à plusieurs sites ; téléphonie, avec les postes, les renvois, les plans d\'appel, le journal des appels, le parc de numéros et la supervision ; infra managée ; et quatre guides destinés aux clients.',
            extranet: 'De nouveaux guides détaillent vos services et vos appels, vos factures et documents, vos postes téléphoniques et vos annuaires.',
          },
        ],
      },
      {
        title: 'Gestion des droits',
        scope: 'internal',
        items: [
          'Le fourre-tout « Autres » de la matrice se déplie en blocs nommés et repliables, au lieu d\'une liste à plat de plus d\'une centaine de lignes.',
          'Chaque niveau d\'accès annonce ce qu\'il accorde sur le bloc consulté (la suppression ici, une action sensible là, rien ailleurs), et un niveau qui n\'apporte rien de plus que celui du dessous n\'est plus proposé.',
          'Le sélecteur de niveau descend jusqu\'au sous-groupe et pilote les cases à cocher de son bloc.',
        ],
      },
      {
        title: 'Notes',
        scope: 'internal',
        items: [
          'Les filtres de la chronologie et le formulaire de saisie ne proposent plus que les catégories de notes que l\'on a le droit de consulter, et la saisie démarre sur l\'une d\'elles.',
        ],
      },
    ],
  },
  {
    version: '2.43.9',
    date: '2026-08-14',
    title: 'Documentation',
    features: [
      {
        title: 'Documentation',
        items: [
          {
            text: 'Un chargement des guides qui échoue n\'est plus rejoué en boucle : il affiche un message et un bouton pour réessayer, au lieu de saturer le serveur et d\'annoncer qu\'aucun guide n\'est disponible.',
            extranet: 'Si les guides ne peuvent pas être chargés, l\'écran le dit et propose de réessayer.',
          },
        ],
      },
    ],
  },
  {
    version: '2.43.8',
    date: '2026-08-14',
    title: 'Documentation',
    features: [
      {
        title: 'Documentation',
        items: [
          {
            text: 'Ouvrir un guide depuis le sommaire ne recharge plus que le volet de lecture : la liste reste en place et la recherche saisie est conservée.',
            extranet: 'Ouvrir un guide ne recharge plus toute la page : le sommaire reste en place.',
          },
        ],
      },
    ],
  },
  {
    version: '2.43.7',
    date: '2026-08-14',
    title: 'Documentation',
    features: [
      {
        title: 'Documentation',
        items: [
          {
            text: 'Les guides s\'affichent de nouveau dans la rubrique Documentation, ainsi que leur contenu à l\'ouverture d\'une fiche.',
            extranet: 'Les guides d\'utilisation s\'affichent de nouveau dans la rubrique Documentation.',
          },
        ],
      },
    ],
  },
  {
    version: '2.43.6',
    date: '2026-08-14',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          {
            text: 'La conversation occupe toute la hauteur de l\'écran au lieu de s\'arrêter à mi-page.',
            scope: 'internal',
          },
          {
            text: 'La zone de saisie s\'agrandit à mesure qu\'on écrit, jusqu\'à dix lignes.',
            scope: 'internal',
          },
        ],
      },
    ],
  },
  {
    version: '2.43.5',
    date: '2026-08-13',
    title: 'Documentation',
    features: [
      {
        title: 'Documentation',
        items: [
          {
            text: 'Une rubrique Documentation rassemble les guides d\'utilisation, chacun n\'apparaissant qu\'aux personnes qui ont accès à ce qu\'il décrit.',
            extranet: 'Une rubrique Documentation rassemble les guides d\'utilisation de votre espace.',
          },
          {
            text: 'L\'en-tête d\'un écran documenté porte un point d\'interrogation qui ouvre le guide correspondant ; le bouton de la barre du haut, lui, ouvre l\'ensemble des guides.',
            extranet: 'Les écrans documentés portent un point d\'interrogation dans leur titre, qui ouvre le guide correspondant.',
          },
        ],
      },
      {
        title: 'Guides disponibles',
        scope: 'internal',
        items: [
          'Manuel du responsable de stock : principe du suivi, entrées, sorties, ajustements, reprise de parc existant, SAV et messages de refus.',
          'Prise en main du back-office, et présentation de l\'espace client.',
        ],
      },
    ],
  },
  {
    version: '2.43.4',
    date: '2026-08-13',
    title: 'Gestion de stock',
    features: [
      {
        title: 'Corriger le stock',
        scope: 'internal',
        items: [
          'L\'ajustement d\'inventaire fonctionne desormais sur le materiel suivi par numero de serie, MAC ou IPEI : on saisit les identifiants trouves et on designe les exemplaires introuvables, ce qui donne une seule ligne au journal avec son auteur et son motif',
          'C\'est aussi la maniere d\'enregistrer un parc existant qu\'aucune commande ne documente : les numeros releves sur le materiel suffisent',
          'Un exemplaire declare manquant sort du stock sans etre efface : son historique reste consultable et un retour le remet en stock s\'il est retrouve',
        ],
      },
      {
        title: 'Reception',
        scope: 'internal',
        items: [
          'Le bouton « Receptionner » ouvre la reception depuis la commande et bascule directement sur le scan, sans passer par un autre onglet ni retrouver la commande dans une liste',
          'Le bouton « Recue » devient « Solder » et annonce ce qu\'il fait : la commande se ferme, aucun materiel n\'entre en stock. Les deux voies ne se distinguaient jusqu\'ici ni par leur nom ni par leur effet annonce',
          'Une commande n\'a plus qu\'une reception ouverte a la fois : deux receptions en parallele pouvaient compter la meme livraison deux fois',
        ],
      },
    ],
  },
  {
    version: '2.43.3',
    date: '2026-08-13',
    title: 'Mes postes',
    features: [
      {
        title: 'Mes postes',
        items: [
          {
            text: 'La fenêtre « Modifier le poste » s\'ouvrait au ras du haut de l\'écran, son titre coupé : le dialogue était rendu dans la grille des cartes, où l\'override global .el-dialog { margin: 0 auto } d\'element-ui.scss annule le décalage haut d\'Element Plus. Même traitement préventif sur les deux fenêtres des annuaires.',
            extranet: 'La fenêtre de modification d\'un poste s\'affiche à nouveau entièrement, centrée dans l\'écran.',
          },
        ],
      },
    ],
  },
  {
    version: '2.43.2',
    date: '2026-08-13',
    title: 'Notes de version',
    features: [
      {
        title: 'Notes de version',
        items: [
          {
            text: 'Les versions publiées sont toutes atteignables : la liste devient une colonne défilante classée par mois, là où la ligne précédente les coupait au-delà de la largeur de la fenêtre.',
            extranet: 'L\'historique des nouveautés se parcourt version par version, dans une colonne classée par mois.',
          },
        ],
      },
    ],
  },
  {
    version: '2.43.1',
    date: '2026-08-13',
    title: 'Notes de version',
    features: [
      {
        title: 'Notes de version',
        scope: 'internal',
        items: [
          'Deux titres de version annonçaient aux clients des modules qui leur sont fermés : « factures d\'achat » et « notifications ». Le titre lu par le client correspond désormais à ce qu\'il constate, celui lu en interne reste complet.',
        ],
      },
    ],
  },
  {
    version: '2.43.0',
    date: '2026-08-13',
    title: 'Gestion de stock',
    features: [
      {
        title: 'Saisie des mouvements',
        scope: 'internal',
        items: [
          'Un seul formulaire de mouvement, identique depuis le tableau de bord et depuis la liste : les deux écrans n\'offraient ni les mêmes champs ni la même saisie',
          'Le client se choisit par son nom, dans les mouvements comme sur la fiche d\'un exemplaire, au lieu de son identifiant interne',
          'L\'exemplaire n\'est demandé que lorsqu\'il doit vraiment être désigné, et la liste ne montre que les exemplaires du produit choisi, en stock',
          'Le stock courant s\'affiche pendant la saisie, au lieu d\'être découvert au moment du refus',
          'La référence d\'un bon de livraison est saisissable depuis le tableau de bord',
          'Un retour lancé depuis le tableau de bord aboutit : le champ obligatoire manquait, et la demande était rejetée à chaque fois',
        ],
      },
      {
        title: 'Fiches produit',
        scope: 'internal',
        items: [
          'Le stock de départ se saisit à la création de la fiche, et apparaît comme une entrée dans l\'historique du produit',
          'Un produit se gère par quantité sauf mention contraire ; le suivi pièce par pièce se réserve au matériel qui porte un numéro de série, une MAC ou un IPEI',
          'Le stock ne se corrige plus depuis la fiche mais par un ajustement d\'inventaire, qui garde trace de l\'auteur et du motif',
          'L\'ajustement d\'inventaire fonctionne aussi sur le matériel suivi pièce par pièce, quand ses exemplaires sont interchangeables',
          'Les champs qui n\'avaient pas à être obligatoires ne le sont plus : le raccourci d\'une marque, le statut d\'un exemplaire',
        ],
      },
      {
        title: 'Réception',
        scope: 'internal',
        items: [
          'Le curseur reste dans le champ de scan entre deux articles, sans avoir à recliquer',
          'Les combinés DECT et les codes internes sont acceptés en saisie manuelle',
          'Réceptionner plusieurs unités d\'un même article enregistre bien chaque unité',
          'Annuler une saisie rend la totalité de ce qu\'elle avait fait entrer',
        ],
      },
      {
        title: 'Fiabilité du stock',
        scope: 'internal',
        items: [
          'Le stock affiché ne peut plus diverger de la réalité : il est tenu par un seul chemin, et recalculé depuis les exemplaires pour le matériel suivi pièce par pièce',
          'Les alertes de rupture et de surstock se déclenchent sur l\'opération qui franchit le seuil, et se referment d\'elles-mêmes au réapprovisionnement',
          'Les écrans de rapports, la recherche de commandes fournisseur et l\'annulation d\'une saisie de réception répondaient en erreur : ils fonctionnent',
          'Les refus expliquent ce qui bloque, là où ils affichaient « erreur interne »',
        ],
      },
    ],
  },
  {
    version: '2.42.3',
    date: '2026-08-12',
    title: 'Gestion des droits et analyse des appels',
    features: [
      {
        title: 'Gestion des droits',
        scope: 'internal',
        items: [
          'La matrice suit désormais l\'organisation du menu : un droit se cherche là où on l\'exerce, sous l\'écran ou l\'onglet correspondant',
          'Le niveau d\'accès se règle écran par écran, et plus seulement par grand domaine',
          'La vérification d\'éligibilité d\'une offre est ouverte à l\'ensemble des rôles internes',
        ],
      },
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          'Quand la période demandée précède le début de l\'enregistrement, l\'écran dit depuis quand les appels sont connus, au lieu de laisser croire à des mois manquants',
          'La comparaison à la période précédente n\'est plus affichée lorsqu\'elle porterait sur des jours antérieurs à l\'enregistrement, où elle annonçait un effondrement qui n\'a pas eu lieu',
        ],
      },
    ],
  },
  {
    version: '2.42.2',
    date: '2026-08-12',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'L\'historique revient dans le panneau, en onglet à côté de la conversation en cours : choisir une conversation y ramène directement',
          'Le choix du modèle et le bouton de nouvelle conversation tiennent de nouveau sur une seule ligne',
        ],
      },
    ],
  },
  {
    version: '2.42.1',
    date: '2026-08-12',
    title: 'Dates et déroulé des appels',
    features: [
      {
        title: 'Périodes',
        items: [
          {
            text: 'Une période démarre bien au jour affiché : les sélecteurs de dates retenaient la veille',
            extranet: 'Les périodes que vous choisissez démarrent bien au jour affiché',
          },
          'Les périodes proposées se recalculent à l\'ouverture, et non au chargement de l\'application : un onglet resté ouvert la nuit proposait encore la veille',
        ],
      },
      {
        title: 'Stock',
        scope: 'internal',
        items: [
          'Les dates des mouvements et des réceptions ne se décalent plus d\'un jour',
        ],
      },
      {
        title: 'Appels de téléphonie fixe',
        items: [
          {
            text: 'Le déroulé d\'un appel émis ne présente plus le poste appelant comme sonnant puis décrochant ; il nomme celui qui appelle et signale quand le correspondant prend la ligne',
            extranet: 'Le détail d\'un appel que vous avez passé indique qui a appelé et quand votre correspondant a décroché',
          },
        ],
      },
    ],
  },
  {
    version: '2.42.0',
    date: '2026-08-12',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Luce s\'ouvre en panneau latéral : on continue à naviguer dans gestion-v2 pendant la conversation, et le fil reste en place d\'un écran à l\'autre',
          'Échap referme le panneau ; l\'historique des conversations reste accessible en plein écran',
        ],
      },
    ],
  },
  {
    version: '2.41.0',
    date: '2026-08-12',
    title: 'Luce, factures d\'achat et téléphonie',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Luce a désormais une signature manuscrite, qui remplace la bulle : en en-tête de sa fenêtre, sur la pastille d\'appel, dans le menu et sur l\'écran d\'accueil de la conversation',
          'L\'en-tête de la fenêtre passe en bandeau ; le bouton plein écran y perd son libellé et rejoint la croix de fermeture, en retrait',
        ],
      },
      {
        title: 'Factures d\'achat',
        scope: 'internal',
        items: [
          'Une aide au survol explique les écrans de rapprochement et de ventilation',
          'Les factures encore jamais ouvertes sont signalées dans le menu et dans la liste',
        ],
      },
      {
        title: 'Téléphonie',
        scope: 'internal',
        items: [
          'Les statistiques d\'un service comptent à partir du jour affiché, et non de la veille',
        ],
      },
    ],
  },
  {
    version: '2.40.1',
    date: '2026-08-12',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Sous Safari, la fenêtre s\'écrasait en hauteur et la conversation se réduisait à une bande',
        ],
      },
    ],
  },
  {
    version: '2.40.0',
    date: '2026-08-12',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Chaque réponse peut être téléchargée en PDF, Word, LibreOffice, CSV ou texte',
          'Les tableaux qu\'elle produit s\'affichent comme de vrais tableaux, et partent tels quels dans le CSV',
        ],
      },
    ],
  },
  {
    version: '2.39.3',
    date: '2026-08-12',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Les écrans qu\'elle cite deviennent cliquables : un clic ouvre la page sans quitter la conversation',
        ],
      },
    ],
  },
  {
    version: '2.39.2',
    date: '2026-08-11',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Les réponses sont mises en forme : gras, listes, titres et extraits de code s\'affichent au lieu des astérisques et accents graves',
        ],
      },
    ],
  },
  {
    version: '2.39.1',
    date: '2026-08-11',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Le logo de Luce s\'affiche enfin sur la pastille et dans la fenêtre : il était demandé sous un nom que le composant d\'icônes ne savait pas résoudre',
          'Une infobulle au survol de la pastille explique à quoi sert Luce, pour qui la découvre sans savoir sur quoi il clique',
        ],
      },
    ],
  },
  {
    version: '2.39.0',
    date: '2026-08-11',
    title: 'Luce',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Luce s\'ouvre en fenêtre depuis n\'importe quel écran, par la pastille en bas à droite, sans quitter ce qu\'on est en train de faire',
          'Elle répond aux questions du type « où est-ce que je modifie ceci ? » en donnant le chemin dans le menu, et s\'en tient aux écrans auxquels on a accès',
          'Elle rédige de nouveau courriers et modèles, ce qu\'une consigne trop stricte l\'empêchait de faire',
          'La page dédiée reste disponible pour les échanges longs et la relecture de l\'historique',
        ],
      },
    ],
  },
  {
    version: '2.38.0',
    date: '2026-08-11',
    title: 'Luce, l\'assistant interne',
    // Le correctif de défilement rend cette release visible côté client : sans
    // titre `extranet`, le nom de l'outil interne serait lu par les clients.
    extranet: 'Confort d\'affichage',
    features: [
      {
        title: 'Luce',
        scope: 'internal',
        items: [
          'Un assistant de conversation s\'ouvre depuis le menu, pour les rôles à qui la permission a été donnée',
          'Les réponses s\'écrivent au fil de leur rédaction, sans attendre qu\'elles soient terminées',
          'Chaque échange est conservé dans un historique personnel, que son auteur est seul à voir et à pouvoir supprimer',
          'Le modèle se choisit avant l\'envoi, du plus rapide au plus complet',
        ],
      },
    ],
    fixes: [
      {
        text: 'Les écrans longs, dont la liste des services d\'un compte, se parcourent de nouveau jusqu\'en bas',
        extranet: 'Le défilement des pages longues fonctionne de nouveau',
      },
    ],
  },
  {
    version: '2.37.3',
    date: '2026-08-11',
    title: 'Appels de téléphonie fixe',
    extranet: 'Vos appels sur plusieurs années',
    features: [
      {
        title: 'Mes appels',
        items: [
          {
            text: 'Les statistiques remontent à cinq ans, avec un pas annuel et les raccourcis correspondants',
            extranet: 'Vos statistiques d\'appels remontent à cinq ans : comparez une année à l\'autre d\'un seul geste',
          },
          {
            text: 'La liste d\'appels couvre un an en ordre chronologique ; les autres tris restent limités à un trimestre, ce que l\'écran annonce',
            extranet: 'La liste de vos appels couvre une année entière',
          },
        ],
      },
    ],
  },
  {
    version: '2.37.2',
    date: '2026-08-10',
    title: 'Appels de téléphonie fixe',
    extranet: 'Vos appels, chiffres à l\'appui',
    features: [
      {
        title: 'Mes appels',
        items: [
          {
            text: 'Le graphe se règle au jour, à la semaine ou au mois, et cliquer une barre restreint la période à cet intervalle',
            extranet: 'Le graphe s\'affiche au jour, à la semaine ou au mois ; cliquer une barre ne garde que cette période',
          },
          {
            text: 'Deux tableaux donnent l\'activité par poste et par file d\'attente : appels, temps de conversation, attente moyenne, part de répondus',
            extranet: 'L\'activité de chaque poste et de chaque file s\'affiche en clair : appels, temps de conversation, attente moyenne',
          },
          {
            text: 'La période consultée est écrite en toutes lettres, avec sa durée et la période de comparaison',
            extranet: 'La période consultée est rappelée en clair, avec sa durée',
          },
        ],
      },
    ],
  },
  {
    version: '2.37.1',
    date: '2026-08-10',
    title: 'Appels de téléphonie fixe',
    extranet: 'Consultez les appels de vos lignes fixes',
    features: [
      {
        title: 'Mes appels',
        items: [
          {
            text: 'L\'écran d\'analyse des appels s\'ouvre désormais aux clients depuis leurs services de téléphonie fixe',
            extranet: 'Vos lignes fixes donnent accès au détail de vos appels : volumes, taux de décrochage, temps d\'attente et déroulé de chaque appel',
          },
        ],
      },
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          'Les appels du jour en cours apparaissent : la dernière journée de la période demandée était exclue',
          'La période se choisit par raccourci et se décale d\'un cran dans un sens ou dans l\'autre',
          'Chaque chiffre de tête porte son écart avec la période précédente, et commande la courbe jour par jour',
          'La répartition par issue filtre la liste d\'un clic',
          'Les colonnes de la liste se trient sur l\'ensemble des appels, et non sur la page affichée',
          'Filtres par file d\'attente, par poste et par numéro appelé, limités à ce que la période contient',
          'Un appel interne abandonné avant la sonnerie n\'est plus compté comme un appel émis',
        ],
      },
    ],
  },
  {
    version: '2.37.0',
    date: '2026-08-10',
    title: 'Hébergement web',
    features: [
      {
        title: 'Hébergement web',
        scope: 'internal',
        items: [
          'Un compte rattaché au mauvais service peut être détaché, ce qui rouvre tous les choix de rattachement',
          'Un client qui possède plusieurs hébergements les retrouve tous sur sa fiche, par un sélecteur de compte',
          'Un compte dont le rattachement a été perdu reste proposé au rattachement depuis la fiche client, au lieu d\'y apparaître grisé',
          'Le rattachement d\'un compte demande une confirmation nommant le client retenu, le client d\'un compte rattaché ne pouvant plus changer ensuite',
        ],
      },
    ],
  },
  {
    version: '2.36.1',
    date: '2026-08-07',
    title: 'Appels de téléphonie fixe',
    features: [
      {
        title: 'Analyse des appels',
        scope: 'internal',
        items: [
          'Un appel d\'un poste vers un collègue est reconnu comme interne, et non plus comme un appel émis',
          'La synthèse décompte les appels internes à part des appels reçus et émis',
          'Les postes sont désignés par leur nom suivi de leur numéro, dans la liste, le détail d\'un appel et l\'export',
          'Un poste renommé porte son nouveau nom sur tout l\'historique',
          'La fenêtre porte le nom du service consulté et son type',
        ],
      },
    ],
  },
  {
    version: '2.36.0',
    date: '2026-08-07',
    title: 'Hébergement web et notifications',
    // Le centre de notifications est réservé au périmètre interne : côté client,
    // seul l'affichage des notes de version change.
    extranet: 'Notes de version',
    features: [
      {
        title: 'Hébergement web',
        scope: 'internal',
        items: [
          'Un compte d\'hébergement dont le rattachement au service a été perdu est signalé comme tel et peut être rattaché de nouveau',
          'Le rattachement se fait sur un service d\'hébergement existant du client, ou sur un nouveau service si aucun ne convient',
          'Le service rattaché à un compte peut être changé, après confirmation',
          'Un service d\'hébergement sans compte est signalé sur la fiche client : le compte s\'y crée ou s\'y rattache directement',
        ],
      },
      {
        title: 'Notifications',
        scope: 'internal',
        items: [
          'Le détail d\'une notification s\'affiche dans la cloche, et plus seulement son titre',
        ],
      },
      {
        title: 'Notes de version',
        items: [
          {
            text: 'La fenêtre Nouveautés s\'ouvre de nouveau à la sortie d\'une version, et « Ne plus afficher » est honoré quelle que soit la façon de la fermer',
            extranet: 'Les nouveautés s\'affichent à chaque nouvelle version, et le choix de ne plus les afficher est respecté',
          },
        ],
      },
    ],
  },
  {
    version: '2.35.0',
    date: '2026-08-07',
    title: 'Carte des clients, hébergement web et téléphonie fixe',
    extranet: 'Corrections d\'affichage',
    features: [
      {
        title: 'Carte des clients',
        scope: 'internal',
        items: [
          'Un clic sur la pastille d\'un site ouvre son aperçu : services actifs, montant mensuel de chacun et total',
          'Les pastilles sont colorées selon la fibre active et portent un anneau distinctif sur les sites Resilientia',
          'Seuls les sites encore servis apparaissent sur la carte',
          'Les numéros de téléphone sont affichés au format français, sans perdre leur zéro initial',
        ],
      },
      {
        title: 'Téléphonie fixe',
        scope: 'internal',
        items: [
          'Nouvel écran d\'analyse des appels fixes, ouvert par le bouton Conso depuis la liste des services',
        ],
      },
      {
        title: 'Hébergement web',
        scope: 'internal',
        items: [
          'Les quotas réels de l\'hébergement et les quotas DNS sont affichés sur la fiche client',
          'La zone DNS est sélectionnée d\'office lorsque le compte n\'en possède qu\'une',
          'Une zone DNS est créée automatiquement à l\'ajout d\'un domaine web',
          'L\'émission d\'un certificat SSL signale son attente au lieu de paraître figée',
        ],
      },
      {
        title: 'Comptabilité fournisseur',
        scope: 'internal',
        items: [
          'Le compte auxiliaire fournisseur se saisit depuis la fiche compte, sur la convention de préfixe 401',
          'Les comptes auxiliaires client et fournisseur sont nommés distinctement',
          'Le compte auxiliaire fournisseur ne s\'affiche plus sur les sites d\'un groupe, où il n\'a pas lieu d\'être',
          'L\'onglet Facturation est masqué sur un compte purement fournisseur',
        ],
      },
      {
        title: 'Stock',
        scope: 'internal',
        items: [
          'Les sélecteurs de fournisseur s\'appuient sur l\'annuaire des comptes',
        ],
      },
      {
        title: 'Diagnostic des erreurs',
        scope: 'internal',
        items: [
          'Une erreur serveur affiche un code court et une référence unique : les transmettre au support permet de retrouver la trace exacte de l\'incident',
          'Nouvelle rubrique « Codes d\'erreur » dans Administration : cause probable, action utilisateur et action support pour chaque code, avec filtre par domaine et recherche',
          'Les messages d\'erreur reprennent la raison réelle du refus (permission manquante, règle de validation) au lieu d\'un message générique',
        ],
      },
      {
        title: 'Déploiements Asterisk',
        scope: 'internal',
        items: [
          'Le mode de déploiement se choisit entre Pull et Push, Pull étant retenu par défaut',
          'L\'historique des déploiements signale celui actuellement en vigueur',
          'La modification des renvois d\'appels est synchronisée automatiquement vers les serveurs du cluster',
        ],
      },
    ],
    fixes: [
      'Huit icônes qui restaient invisibles s\'affichent de nouveau',
      {
        text: 'Le signalement d\'un problème affiche le code d\'erreur et la référence à transmettre au support',
        extranet: 'En cas d\'erreur, le message affiche désormais une référence à communiquer au support',
      },
      { text: 'La fenêtre d\'analyse des appels s\'ouvrait vide et en plein écran', scope: 'internal' },
      { text: 'L\'annuaire affiche son compteur de contacts et ses sélecteurs sont complets', scope: 'internal' },
      { text: 'Les notes d\'un compte s\'affichent en entier, sans repli', scope: 'internal' },
      { text: 'Le montant réellement facturé d\'un service et son rythme de facturation sont affichés', scope: 'internal' },
      { text: 'La création d\'un accès extranet est proposée sur les contacts principaux', scope: 'internal' },
      { text: 'Les signalements détectés en doublon s\'affichent correctement', scope: 'internal' },
      { text: 'Renforcement des contrôles d\'accès et des contrôles de saisie côté serveur', scope: 'internal' },
    ],
  },
  {
    version: '2.34.0',
    date: '2026-07-29',
    title: 'Libellés de service et factures d\'achat',
    // Les factures d'achat sont un module interne : le seul item visible côté
    // client porte sur le libellé de service, le titre doit s'y tenir.
    extranet: 'Libellés de service',
    features: [
      {
        title: 'Rapprochement d\'une facture reçue avec son fournisseur',
        scope: 'internal',
        items: [
          'Un filtre « non rattachées » et une action « Rapprocher » sont disponibles depuis la liste comme depuis le détail d\'une facture d\'achat.',
          'La proposition de rapprochement est pré-remplie à partir de la facture et de SIRENE : sélection d\'un fournisseur existant ou création pré-remplie.',
          'Le justificatif est distingué selon son état (consultable, en attente de rapprochement, aucun), avec un dépôt accepté jusqu\'à 20 Mo.',
        ],
      },
    ],
    improvements: [
      'Le libellé d\'un service s\'affiche désormais sur plusieurs lignes dans les écrans de création et de modification : il se lit en entier sans avoir à faire défiler le champ.',
    ],
  },
  {
    version: '2.33.0',
    date: '2026-07-20',
    title: 'Factures d\'achat : validation (acceptation / refus)',
    features: [
      {
        title: 'Circuit de validation des factures reçues',
        scope: 'internal',
        items: [
          'Sur le détail d\'une facture d\'achat reçue, deux actions « Accepter » et « Refuser » (avec motif) permettent de valider la pièce avant sa ventilation comptable.',
          'Un badge indique l\'état de validation (à valider / acceptée / refusée), et le motif est affiché sur les pièces refusées.',
        ],
      },
    ],
  },
  {
    version: '2.32.0',
    date: '2026-07-20',
    title: 'Comptabilité & notifications',
    features: [
      {
        title: 'Comptabilité : Codes TVA',
        scope: 'internal',
        items: [
          'Nouvel écran de paramétrage « Codes TVA » (référentiel des codes Isacompta).',
          'Le champ « Code TVA » de la ventilation des factures d\'achat et de la saisie de lignes devient une liste stricte, avec renseignement automatique du taux à la sélection.',
        ],
      },
      {
        title: 'Centre de notifications',
        scope: 'internal',
        items: [
          'Cloche et centre de notifications in-app, gestion des préférences par type et par canal, et écran d\'administration du catalogue des types.',
        ],
      },
    ],
  },
  {
    version: '2.31.4',
    date: '2026-07-17',
    title: 'Correctifs mineurs',
    features: [],
    fixes: [
      { text: 'Détail d\'une facture d\'achat et d\'un poste SIP : plus de requête superflue lors du retour vers la liste.', scope: 'internal' },
    ],
  },
  {
    version: '2.31.3',
    date: '2026-07-13',
    title: 'Factures : badge « Annulée »',
    features: [
      {
        title: 'Factures annulées identifiées dans la liste',
        scope: 'internal',
        items: [
          'Les factures annulées (conservées pour l\'historique mais exclues du chiffre d\'affaires) sont désormais signalées par un badge « Annulée » dans la liste des factures d\'un compte.',
        ],
      },
    ],
  },
  {
    version: '2.31.2',
    date: '2026-07-12',
    title: 'Provisioning : vocabulaire uniformisé',
    features: [
      {
        title: 'Terminologie « modèle / séquence » finalisée',
        scope: 'internal',
        items: [
          'Les derniers écrans qui parlaient encore de « template » (édition d\'un modèle, messages) utilisent désormais « modèle » ; les libellés techniques bruts (stop_on_failure, Halt, « Push raw ») sont remplacés par des termes clairs.',
          'Les boutons de lancement disent partout « Simuler » (test) et « Appliquer » (exécution réelle), qu\'il s\'agisse d\'un modèle, d\'une séquence ou d\'une commande brute.',
        ],
      },
    ],
  },
  {
    version: '2.31.1',
    date: '2026-07-12',
    title: 'Provisioning : actions regroupées dans un menu',
    features: [
      {
        title: 'Fiche équipement plus lisible',
        scope: 'internal',
        items: [
          'Les actions d\'un équipement (Simuler / Appliquer un modèle, une séquence ou une commande brute) sont désormais regroupées dans un menu « Provisionner » en haut de la fiche, à la place de l\'ancien onglet dédié.',
          'Le bouton « Démarquer cet équipement » est déplacé au bas de l\'onglet Connexion.',
        ],
      },
    ],
  },
  {
    version: '2.31.0',
    date: '2026-07-10',
    title: 'Provisioning : statuts cohérents, suivi des jobs et corrections',
    features: [
      {
        title: 'Affichage des statuts unifié et suivi en temps réel',
        scope: 'internal',
        items: [
          'Les statuts des jobs (en cours, en file, succès, échec…) s\'affichent désormais avec les mêmes libellés français et les mêmes couleurs sur tous les écrans (un même job ne change plus de couleur en naviguant).',
          'Le détail d\'un job se met à jour automatiquement tant qu\'il est en cours (plus besoin de rafraîchir manuellement).',
        ],
      },
    ],
    fixes: [
      { text: 'Commande brute en mode application : la demande de re-authentification s\'affiche correctement (au lieu d\'une erreur générique).', scope: 'internal' },
      { text: 'Recherche d\'équipements : saisie plus fluide (moins de requêtes, plus de résultat périmé qui s\'affiche).', scope: 'internal' },
    ],
  },
  {
    version: '2.30.4',
    date: '2026-07-10',
    title: 'Provisioning : aperçu des commandes et suivi des jobs',
    features: [
      {
        title: 'Aperçu avant lancement + statut des jobs à jour',
        scope: 'internal',
        items: [
          'Le lancement d\'une séquence propose une étape « Aperçu » : les commandes / payloads qui seraient envoyés à l\'équipement sont affichés étape par étape, avant tout envoi.',
          'La liste des jobs se met à jour automatiquement tant qu\'un job est en cours ; annuler un job déjà terminé rafraîchit proprement l\'affichage au lieu d\'une erreur.',
        ],
      },
    ],
  },
  {
    version: '2.30.3',
    date: '2026-07-10',
    title: 'Provisioning : actions d\'un équipement regroupées',
    features: [
      {
        title: 'Boutons d\'exécution plus lisibles',
        scope: 'internal',
        items: [
          'Sur la fiche d\'un équipement, les actions sont regroupées par type (Modèle, Séquence, Commande brute), chacune avec « Simuler » (test) et « Appliquer » (exécution réelle), au lieu de six boutons aux libellés disparates.',
        ],
      },
    ],
  },
  {
    version: '2.30.2',
    date: '2026-07-10',
    title: 'Provisioning : filtre modèle/séquence et action plus claire',
    features: [
      {
        title: 'Filtrer et repérer les actions',
        scope: 'internal',
        items: [
          'La liste Provisioning propose un filtre « Tous / Modèles / Séquences ».',
          'Dans la liste des équipements, l\'action principale indique désormais clairement qu\'elle ouvre la fiche pour pousser des modèles ou lancer des séquences.',
        ],
      },
    ],
  },
  {
    version: '2.30.1',
    date: '2026-07-10',
    title: 'Provisioning : avertissement de séquence plus clair',
    features: [
      {
        title: 'Message explicite sur le retour arrière',
        scope: 'internal',
        items: [
          'À l\'édition d\'une séquence, quand une étape utilise un modèle sans annulation définie, l\'avertissement explique désormais la conséquence (étape non annulable en cas d\'échec) et les deux façons de corriger.',
        ],
      },
    ],
  },
  {
    version: '2.30.0',
    date: '2026-07-10',
    title: 'Provisioning : paramètres par étape et vocabulaire simplifié',
    features: [
      {
        title: 'Paramètres de séquence par étape',
        scope: 'internal',
        items: [
          'Au lancement d\'une séquence, les paramètres sont désormais saisis par étape : chaque étape a ses propres valeurs, y compris quand deux étapes utilisent le même modèle.',
          'Vocabulaire simplifié dans l\'écran Provisioning : « modèle » remplace « template » et « séquence » remplace « composite ».',
        ],
      },
    ],
  },
  {
    version: '2.29.0',
    date: '2026-07-10',
    title: 'Provisioning : exécution des modèles composites',
    features: [
      {
        title: 'Lancer un modèle composite depuis un équipement',
        scope: 'internal',
        items: [
          'Un modèle composite (enchaînement de plusieurs modèles) peut désormais être exécuté directement depuis la fiche d\'un équipement, en simulation ou en application réelle.',
          'Le formulaire de paramètres est pré-rempli automatiquement à partir des modèles enchaînés : chaque variable n\'est saisie qu\'une seule fois, même si plusieurs étapes l\'utilisent.',
          'Si deux étapes attendent la même variable dans des formats incompatibles, le lancement est bloqué avec un message explicite.',
          'L\'exécution en mode application demande une confirmation du mot de passe, comme pour un modèle simple.',
        ],
      },
    ],
    improvements: [
      { text: 'Les modèles composites sont désormais clairement identifiés dans la liste (badge dédié) et s\'éditent avec leurs étapes déjà renseignées.', scope: 'internal' },
    ],
    fixes: [
      { text: 'Édition des services d\'un compte enfant depuis la fiche du compte parent : les modifications ciblent désormais le bon compte.', scope: 'internal' },
    ],
  },
  {
    version: '2.28.0',
    date: '2026-07-09',
    title: 'Factures d\'achat et stabilisation provisioning',
    features: [
      {
        title: 'Factures d\'achat fournisseurs',
        scope: 'internal',
        items: [
          'Nouvelle interface de saisie et consultation des factures d\'achat fournisseurs : création, modification, suppression et recherche',
          'Ventilation comptable automatique des factures par comptes configurés',
          'Upload et gestion des justificatifs PDF',
        ],
      },
      {
        title: 'Paramétrage comptable',
        scope: 'internal',
        items: [
          'Nouvelle interface d\'administration du paramétrage comptable : plan comptable, journaux et taux de TVA',
        ],
      },
    ],
    fixes: [
      { text: 'Provisioning : le bouton d\'enregistrement des templates composites est désormais bloqué si la configuration est incohérente (rollback demandé sans plan d\'annulation)', scope: 'internal' },
    ],
  },
  {
    version: '2.27.2',
    date: '2026-07-07',
    title: 'Correctif : panneau d\'aide et retour de provisioning',
    features: [
      {
        title: 'Le panneau d\'aide affiche à nouveau son contenu',
        scope: 'internal',
        items: [
          'Le panneau d\'aide de l\'écran Templates Provisioning affichait par erreur « La fiche d\'aide est vide » même quand le contenu était bien récupéré côté serveur. Corrigé : la fiche s\'affiche à nouveau normalement.',
        ],
      },
      {
        title: 'Statut du bouton Provisionner cohérent avec le vrai résultat',
        scope: 'internal',
        items: [
          'Sur la modale d\'édition d\'un lien télécom, le bouton « Provisionner » n\'affichait pas toujours le bon message de retour (le dispatch était pourtant OK côté serveur). Corrigé : le toast reflète désormais l\'état réel : job dispatché avec identifiant, skipped avec raison précise, ou failed avec raison précise.',
        ],
      },
    ],
  },
  {
    version: '2.27.1',
    date: '2026-07-07',
    title: 'Provisioning : déclenchement depuis les liens télécom',
    features: [
      {
        title: 'Bouton « Provisionner » sur le dialogue d\'édition d\'un lien',
        scope: 'internal',
        items: [
          'Un nouveau bouton « Provisionner » est ajouté à la modale d\'édition d\'un lien télécom. Il permet à un opérateur avec la permission dédiée de déclencher le template mappé au type de lien depuis la config Git, soit en mode dry-run pour tester, soit en mode apply pour pousser réellement sur l\'équipement. La réponse indique clairement l\'état : job dispatché avec identifiant, skippé avec raison précise (kill-switch, template pas activé, etc.) ou refusé (permission manquante, variable requise absente). Retry possible à volonté sans casser le lien.',
        ],
      },
      {
        title: 'Toggle « Déclenchable via Link » sur l\'écran template',
        scope: 'internal',
        items: [
          'Le formulaire d\'édition d\'un template Provisioning affiche désormais un switch « Déclenchable via Link » (visible aux admins avec la permission distincte). Active/désactive à chaud si le template peut être déclenché automatiquement depuis la création d\'un lien télécom. Double garde-fou volontaire avec la config Git : il faut deux signatures (dev qui commit le template_tag + admin qui active ici) pour qu\'un template devienne triggerable. La compromission d\'un seul intervenant ne suffit donc pas.',
        ],
      },
    ],
  },
  {
    version: '2.27.0',
    date: '2026-07-06',
    title: 'Fiches contacts : contacts partagés entre plusieurs comptes',
    features: [
      {
        title: 'On vous demande avant de modifier un contact partagé',
        items: [
          'Quand vous modifiez un contact rattaché à plusieurs comptes, l\'application vous prévient désormais et vous laisse choisir : appliquer la modification à tous les comptes concernés, ou créer un contact dédié au compte en cours. Dans ce cas, les autres comptes conservent le contact d\'origine.',
        ],
      },
    ],
  },
  {
    version: '2.26.1',
    date: '2026-07-03',
    title: 'Provisioning : fiche d\'aide + sélecteur Platform',
    features: [
      {
        title: 'Bouton d\'aide sur l\'écran Templates',
        scope: 'internal',
        items: [
          'Un bouton d\'aide (icône « ? ») est ajouté dans l\'en-tête de l\'écran Templates Provisioning. Il ouvre un panneau latéral qui présente la fiche d\'utilisation complète : structure d\'un template atomique, création d\'un composite, lancement (dry-run et apply), supervision des jobs, reprise après échec, cas d\'erreur courants. La fiche est servie depuis le backend et se met à jour dès qu\'une révision de doc est déployée, sans rebuild du frontend.',
        ],
      },
      {
        title: 'Champ Platform passé en sélecteur',
        scope: 'internal',
        items: [
          'Sur le formulaire de création/édition d\'un template atomique, le champ Platform devient une liste déroulante filtrable (Linux, Windows, Cisco IOS, JunOS, pfSense, MikroTik RouterOS). Une valeur libre reste saisissable pour couvrir un vendor non listé. Correction annexe : la valeur MikroTik RouterOS était absente de la liste des platforms proposée sur le filtre asset alors qu\'elle pilote le safe-apply. Elle est désormais présente et alignée entre les 2 écrans.',
        ],
      },
    ],
  },
  {
    version: '2.26.0',
    date: '2026-07-03',
    title: 'Provisioning : composition de modèles multi-étapes',
    features: [
      {
        title: 'Templates composites',
        scope: 'internal',
        items: [
          'Nouveau bouton « Nouveau composite » sur l\'écran des templates Provisioning : le Responsable infra peut désormais assembler plusieurs templates atomiques dans un ordre défini, avec un comportement halt-on-failure paramétrable par étape. Cas d\'usage cible : création d\'un lien L2 MikroTik enchaînant 4 commandes ROS.',
          'Édition d\'un composite : sélection filtrable des templates atomiques enfants, réordonnancement par boutons monter/descendre, tag visuel indiquant les enfants avec undo disponible, option requires_rollback (undo opt-in en sens inverse en cas d\'échec mid-parcours).',
          'Affichage distinct dans la liste : les composites apparaissent avec un tag « composite » et une icône dédiée, la colonne Platform affiche « n/a » (le composite hérite du platform de chaque enfant à l\'exécution).',
        ],
      },
      {
        title: 'Journal d\'exécution : hiérarchie composite',
        scope: 'internal',
        items: [
          'Le détail d\'un job composite_root affiche désormais une carte « Composite orchestrateur » avec l\'agrégation colorée des statuts (success/failed/queued/…) et le tableau ordonné des étapes enfants (chaque ligne cliquable vers son propre détail).',
          'Bouton « Reprendre » actif sur un composite failed : crée un nouveau run à partir de la 1ère étape échouée, marque les étapes déjà réussies comme skipped. Réservé au déclencheur initial du run ou à un super-admin.',
        ],
      },
    ],
  },
  {
    version: '2.25.7',
    date: '2026-07-03',
    title: 'Correction visibilité des notes de version côté extranet',
    features: [
      {
        title: 'Notes de version',
        scope: 'internal',
        items: [
          'La note technique « Configuration eCampaign » (v2.25.5) n\'apparaît plus dans l\'espace client : elle est désormais réservée à l\'interface d\'administration.',
        ],
      },
    ],
  },
  {
    version: '2.25.6',
    date: '2026-07-01',
    title: 'Comptes : nom du contact et raison sociale ne se mélangent plus',
    features: [
      {
        title: 'Création / duplication de compte',
        scope: 'internal',
        items: [
          'À la création d\'un compte et à l\'ajout d\'un sous-compte, le formulaire enregistre désormais le bon champ selon la forme juridique : nom + prénom pour un particulier, raison sociale pour une société ou une association. Fini le nom affiché en double du type « NOM Prénom Raison sociale ».',
        ],
      },
    ],
  },
  {
    version: '2.25.5',
    date: '2026-06-29',
    title: 'Configuration eCampaign : ouverture du dialog corrigée',
    features: [
      {
        title: 'eCampaign : configuration',
        scope: 'internal',
        items: [
          'L\'écran de configuration d\'un service eCampaign s\'ouvre désormais correctement quand le compte n\'a pas encore été activé (admin et extranet)',
        ],
      },
    ],
  },
  {
    version: '2.25.3',
    date: '2026-06-10',
    title: 'Correctifs gestion de stock : navigation et filtres',
    features: [
      {
        title: 'Gestion de stock : corrections',
        scope: 'internal',
        items: [
          'Clic sur les alertes « rupture / stock bas » du tableau de bord : ne plante plus et ouvre la liste des produits filtrée sur le bon statut',
          'Bouton « Retour » depuis le scan de réception : redirige correctement vers la liste des réceptions',
          'Le filtre de statut transmis depuis le tableau de bord est désormais réellement appliqué à la liste des produits',
          'Retour automatique à la première page lors d\'un changement de filtre (produits et exemplaires) : fini la liste vide trompeuse quand on filtrait depuis une page lointaine',
        ],
      },
    ],
  },
  {
    version: '2.15.2',
    date: '2026-06-03',
    title: 'Provisioning Sprint 2 : safe-apply Mikrotik et harmonisation interface',
    features: [
      {
        title: 'Provisioning : Safe-apply Mikrotik',
        scope: 'internal',
        items: [
          'Workflow complet sur RouterOS : prepare backup → apply → ping de santé → confirm OU rollback automatique',
          'Watchdog Mikrotik (script bootstrap « gestion-confirme-modif ») : si confirm absent en 5 min, retour automatique à la configuration précédente',
          'Toggle « safe-apply » pré-coché dans la modale d\'exécution sur les assets Mikrotik',
        ],
      },
      {
        title: 'Provisioning : assets',
        scope: 'internal',
        items: [
          'Nom personnalisé des assets (distinct du serial) pour faciliter l\'identification dans les listes',
          'Bouton « Smoke test » sur /infra/assets : vérification rapide de la connexion sans exécuter de template',
          'Redirection automatique vers le détail complet du job après une exécution',
        ],
      },
      {
        title: 'Provisioning : configuration REST',
        scope: 'internal',
        items: [
          'Champs Endpoint et Méthode HTTP ajoutés à l\'onglet Connexion REST (support des endpoints personnalisés)',
        ],
      },
      {
        title: 'Interface : harmonisation',
        scope: 'internal',
        items: [
          'Composant TableActionButton : harmonisation des boutons d\'action dans les tableaux /infra (icônes, variants de couleur)',
        ],
      },
      {
        title: 'Stock : inventaire',
        scope: 'internal',
        items: [
          'Filtre « managed_filter » sur /inventaire : masquer ou afficher uniquement le stock géré par le provisioning',
        ],
      },
    ],
    fixes: [
      { text: 'Provisioning : ProvisioningAssetDetail hydrate correctement la connection_config imbriquée (driver SSH/REST)', scope: 'internal' },
      { text: 'Provisioning : champ Utilisateur REST API désormais exposé dans la modale « Marquer un asset »', scope: 'internal' },
      { text: 'Provisioning : modale de re-authentification déclenchée correctement sur 403 fresh_auth_required dans ProvisioningRunModal (pattern aligné sur RawPushModal)', scope: 'internal' },
    ],
  },
  {
    version: '2.15.1',
    date: '2026-06-02',
    title: 'Provisioning : secrets, génération SSH, jobs et stabilisation stock',
    features: [
      {
        title: 'Provisioning : jobs et timeline',
        scope: 'internal',
        items: [
          'Liste et détail des jobs provisioning : statut, durée, étapes',
          'Timeline visuelle des étapes d\'exécution avec horodatage et résultat par étape',
        ],
      },
      {
        title: 'Provisioning : coffre des secrets',
        scope: 'internal',
        items: [
          'Interface complète de gestion des secrets : liste, création, rotation, suppression',
          'Saisie sécurisée des données sensibles (masquage à l\'affichage, jamais retourné en clair par l\'API après création)',
        ],
      },
      {
        title: 'Provisioning : génération de paires SSH',
        scope: 'internal',
        items: [
          'Génération directement dans l\'interface : Ed25519 (recommandé) ou RSA-4096',
          'Téléchargement immédiat de la clé privée et stockage côté coffre de la clé publique',
        ],
      },
      {
        title: 'Provisioning : marquage des assets',
        scope: 'internal',
        items: [
          'Modale « Marquer un asset » depuis /infra/assets : déclare un asset existant du stock comme managé',
          'Configuration de la connexion (SSH ou REST API avec champ Utilisateur exposé)',
        ],
      },
      {
        title: 'Stock : catalogue',
        scope: 'internal',
        items: [
          'Auto-fill du nom produit (marque + modèle) lors de la création',
          'Harmonisation des icônes du catalogue',
        ],
      },
    ],
    fixes: [
      { text: 'SIP : chargement des équipements disponibles ciblait le bill au lieu du compte (correction du périmètre)', scope: 'internal' },
      { text: 'Stock : erreur duplicate key lors de l\'usage simultané de setup() et useStockFormatters (4 fichiers fusionnés)', scope: 'internal' },
      { text: 'Stock : bouton « Éditer » manquant sur la liste des produits du catalogue', scope: 'internal' },
      { text: 'Stock : useStockFormatters exposé via setup() pour rendre les formatters disponibles dans le render context', scope: 'internal' },
      { text: 'Comptes : site enfant, lecture de raison_sociale depuis compte_comptes (et plus depuis le contact partagé)', scope: 'internal' },
      { text: 'Interface : SectionHeader robuste à un champ icon manquant + restauration des icônes sur les vues à onglets', scope: 'internal' },
      { text: 'Sidebar Infra managée : suppression de la redondance des labels', scope: 'internal' },
    ],
  },
  {
    version: '2.15.0',
    date: '2026-05-30',
    title: 'Moteur de provisioning : interface des assets, templates et push raw',
    features: [
      {
        title: 'Gestion des assets managés',
        scope: 'internal',
        items: [
          'Nouveau menu « Infra managée » : liste des assets sous provisioning, filtres par statut/type',
          'Détail asset : onglets Identité (lecture seule depuis le stock) et Connexion (formulaire driver-conditionné SSH ou REST API)',
          'Permissions granulaires via le rôle « provisioning operator » (assets, templates, secrets, jobs)',
        ],
      },
      {
        title: 'Templates et exécution',
        scope: 'internal',
        items: [
          'Système de templates Mustache pour générer les configurations réseau',
          'Modale d\'exécution avec aperçu (dry-run) du script généré avant apply',
          'Onglet « Historique » : timeline des exécutions passées avec statut et payload',
        ],
      },
      {
        title: 'Actions ad-hoc',
        scope: 'internal',
        items: [
          'Modale « Push raw » : envoi direct d\'une commande sur un asset sans passer par un template (debug, intervention ponctuelle)',
        ],
      },
    ],
  },
  {
    version: '2.14.6',
    date: '2026-05-29',
    title: 'Système d\'erreurs structuré, stabilisation post-migration icônes et bouton Conso',
    features: [
      {
        title: 'Gestion structurée des erreurs',
        scope: 'internal',
        items: [
          'Chaque erreur dispose désormais d\'un code unique et d\'une référence interne, affichés à l\'utilisateur',
          'Page d\'administration dédiée pour consulter, rechercher et filtrer l\'historique des erreurs par code/référence',
        ],
      },
      {
        title: 'Interface SIP & Mobiluce',
        scope: 'internal',
        items: [
          'Refonte complète de l\'écran de gestion des comptes SIP & Mobiluce',
          'Champ TVA active déplacé du contact partagé vers le compte (cohérence multi-comptes, fix vcard Mobiluce associé)',
          'Adresses MAC affichées sans séparateurs avec indicateur visuel des doublons par nom client',
          'Reformat automatique des MAC avec \':\' en édition de lien',
        ],
      },
      {
        title: 'Consultation conso (espace commercial)',
        scope: 'internal',
        items: [
          'Nouveau bouton « Conso » lecture seule sur la liste de service : les commerciaux (droit browse stats / browse cdr) peuvent consulter la consommation d\'une ligne mobile sans pouvoir la modifier',
        ],
      },
      {
        title: 'Saisie formulaires',
        scope: 'internal',
        items: [
          'CRUD dialog : avertissement non-bloquant si une valeur historique ne passe pas la validation mais n\'a pas été modifiée (évite de bloquer une fiche sur un champ saisi avant durcissement de la règle)',
        ],
      },
    ],
    improvements: [
      { text: 'Page Statistiques verrouillée sur la seule permission « browse stats » (plus de fallback implicite)', scope: 'internal' },
      { text: 'Nettoyage : suppression de 4 méthodes obsolètes (clientgroups/*) dans le client API + tests associés retirés', scope: 'internal' },
    ],
    fixes: [
      { text: 'SIREN : suppression de la validation asynchrone (endpoint check-siren retiré côté backend)', scope: 'internal' },
      { text: '11 issues GitLab corrigées : annuaire extranet, permission P1, modification de compte, gestion SIP, correctifs UX divers', scope: 'internal' },
      { text: 'Build SCSS : accolades orphelines retirées (index.scss, element-ui.scss) + templates corrompus restaurés', scope: 'internal' },
      { text: 'Icônes : ReferenceError post-migration @iconify corrigée sur PageHeader et tags globaux', scope: 'internal' },
      { text: 'Composants manquants ajoutés au registre global : PageHeader, Pagination, AdminTable, Icon', scope: 'internal' },
      { text: 'Icônes globales restaurées + guard compteId ajouté + import PageHeader corrigé', scope: 'internal' },
    ],
  },
  {
    version: '2.14.5',
    date: '2026-05-13',
    title: 'Intégration Mobiluce complète et migration système d\'icônes',
    features: [
      {
        title: 'Intégration Mobiluce',
        scope: 'internal',
        items: [
          'Authentification Mobiluce intégrée via le contact principal du compte (SSO)',
          'Types d\'équipements et provisions synchronisés automatiquement depuis Mobiluce',
          'Dashboard Mobiluce avec KPI : lignes, marges, analyse de flotte',
          'Assistant guidé pour les mouvements de stock Mobiluce (création, réception, retour)',
        ],
      },
      {
        title: 'Système d\'icônes',
        scope: 'internal',
        items: [
          'Migration complète de vue-material-design-icons vers @iconify/vue sur le module Stock (45 fichiers)',
          'Chargement asynchrone des icônes : démarrage plus rapide des pages et meilleur cache navigateur',
        ],
      },
      {
        title: 'Hébergement : administration',
        scope: 'internal',
        items: [
          'Menu « Hébergement » masqué dans l\'administration quand aucun compte HestiaCP n\'est configuré (similaire au comportement côté extranet ajouté en 2.14.4)',
        ],
      },
    ],
    fixes: [
      { text: 'Sécurité : la route admin /hosting est restreinte aux comptes internes uniquement', scope: 'internal' },
      { text: 'Stock : corrections de lint et d\'imports post-migration @iconify (40 + 29 fichiers nettoyés)', scope: 'internal' },
    ],
  },
  {
    version: '2.14.4',
    date: '2026-04-29',
    title: 'Refonte stock + monitoring SIP temps réel + comptes Mobiluce',
    features: [
      {
        title: 'Stock & téléphonie',
        scope: 'internal',
        items: [
          'Monitoring SIP temps réel : affichage instantané du statut des postes (registered / unregistered) avec mise à jour en continu',
          'Écrans de réception fournisseur : scan code-barres ou saisie manuelle des produits reçus, intégration au mouvement de stock',
        ],
      },
      {
        title: 'Comptes Mobiluce',
        scope: 'internal',
        items: [
          'Login Mobiluce délégué au contact principal du compte (plus de saisie manuelle d\'identifiants)',
          'Désactivation automatique du bouton Mobiluce sur les comptes sans téléphonie',
        ],
      },
      {
        title: 'Hébergement : visibilité côté client',
        scope: 'extranet',
        items: [
          'Le menu « Hébergement » disparaît automatiquement dans l\'espace client quand aucun compte HestiaCP n\'est rattaché',
        ],
      },
    ],
    improvements: [
      { text: 'Matrice de permissions dissociée : droits internes et extranet séparés, HestiaCP sorti à part', scope: 'internal' },
      { text: 'Hébergement : dialog de rattachement adapté au nouveau pivot bien_biens', scope: 'internal' },
    ],
    fixes: [
      { text: 'Contacts : suppression d\'un contact principal ou facturation bloquée côté UI, message d\'erreur API affiché clairement', scope: 'internal' },
      { text: 'Contacts : blocage sur l\'indicatif téléphone retiré, formats internationaux acceptés', scope: 'internal' },
      { text: 'Router : rechargement automatique de la page sur ChunkLoadError après un déploiement (évite les erreurs de chargement sur les onglets restés ouverts)', scope: 'internal' },
    ],
  },
  {
    version: '2.14.3',
    date: '2026-04-24',
    title: 'Hébergement sur-mesure, comptes FTP par domaine et identifiants par mail',
    features: [
      {
        title: 'Hébergement : packages sur-mesure par client',
        scope: 'internal',
        items: [
          'Chaque client dispose désormais de son propre package HestiaCP (plus de catalogue partagé) : quotas entièrement personnalisables (disque, bande passante, domaines web, alias, mails, boîtes, bases, cron, SSL)',
          'Nouvel onglet « Quotas & accès » dans la fiche Hébergement de chaque client pour éditer les quotas à la volée',
          'Panneau Hosting → Packages : devient une vue diagnostic en lecture seule (la gestion passe désormais par la fiche de chaque client)',
        ],
      },
      {
        title: 'Hébergement : envoi des identifiants par mail',
        items: [
          'À la création d\'un hébergement, les identifiants (panneau + FTP/SFTP + hostname) sont envoyés automatiquement par mail à l\'adresse choisie (fallback sur le contact admin puis propriétaire)',
          'Bouton « Réinitialiser & envoyer » pour regénérer le mot de passe principal à tout moment',
        ],
      },
      {
        title: 'Hébergement : sous-comptes FTP par domaine',
        items: [
          'Nouveau bouton « Comptes FTP » sur chaque domaine web : créer, supprimer et réinitialiser les sous-comptes FTP additionnels',
          'Identifiants FTP envoyés automatiquement par mail à l\'adresse de destination (création et reset)',
          'Disponible aussi depuis l\'espace client : le client peut gérer ses propres comptes FTP en autonomie',
        ],
      },
    ],
    improvements: [
      { text: 'Formulaire serveur HestiaCP : ajout des champs SSH (utilisateur, port, clé privée) pour permettre la création des packages par client via SFTP', scope: 'internal' },
    ],
    fixes: [
      { text: 'Saisie TVA : le numéro de TVA intracommunautaire n\'est plus obligatoire côté formulaire (création, duplication et facturation). L\'API accepte aussi un champ vide.', scope: 'internal' },
      { text: 'Router : après un déploiement, les onglets ouverts sont automatiquement rechargés au prochain changement de route pour éviter les erreurs de chargement de chunks (garde-fou contre boucle de rechargement)', scope: 'internal' },
    ],
  },
  {
    version: '2.5.0',
    date: '2026-03-29',
    title: 'Offres commerciales, tâches et corrections annuaires',
    features: [
      {
        title: 'Offres commerciales',
        scope: 'internal',
        items: [
          'Création et suivi d\'offres commerciales avec engagement 12/24/36/60 mois',
          'Workflow complet : finaliser, signer, abandonner, rouvrir, cloner une offre',
          'Génération PDF des offres signées',
          'Gestion des sections par site d\'installation avec vérification d\'éligibilité par adresse',
          'Ajout de produits et prestations dans chaque section, avec liens obligatoires/optionnels',
          'Pipeline de production : suivi de l\'avancement des offres signées avec validation/refus de tâches',
          'Dashboard commercial : KPI (offres brouillon/finalisées/signées, CA signé, taux de conversion)',
          'Administration du catalogue : produits, prestations, conditions contractuelles et étapes de production',
        ],
      },
      {
        title: 'Tâches',
        scope: 'internal',
        items: [
          'Système de tâches génériques avec priorités (basse, normale, haute, urgente)',
          'Attribution et suivi des tâches par utilisateur',
          'Rappels automatiques avec notification',
        ],
      },
    ],
    improvements: [
      { text: 'Éligibilité : autorisation de remontée des liens configurable par type de lien depuis l\'administration', scope: 'internal' },
      'Annuaire extranet : le compteur de contacts affiche maintenant le bon nombre',
      'Annuaire extranet : tri alphabétique par défaut',
    ],
    fixes: [
      { text: 'Éligibilité : correction des permissions pour les rôles eligibility prosoluce / max (rôles traités comme des permissions)', scope: 'internal' },
      { text: 'Éligibilité : retrait de l\'accès au contrôleur Prosoluce interne pour le rôle eligibility prosoluce', scope: 'internal' },
      'Annuaire extranet : correction du filtrage par groupe de contacts',
      'Annuaire extranet : correction d\'une erreur lors du chargement sans mot-clé',
    ],
  },
  {
    version: '2.4.0',
    date: '2026-03-27',
    title: 'Zones de couverture, annuaires extranet et améliorations hébergement',
    features: [
      {
        title: 'Zones de couverture',
        scope: 'internal',
        items: [
          'Nouvel onglet « Zones » dans l\'administration des liens pour gérer les zones de couverture (création, modification, suppression, couleur KMZ)',
          'Upload et gestion des fichiers KMZ directement depuis l\'onglet Fournisseurs',
          'Superposition des zones de couverture sur la carte des clients, avec sélection du fournisseur et légende interactive',
          'Possibilité de sélectionner une zone dans le formulaire de création/modification des types de lien',
        ],
      },
    ],
    improvements: [
      { text: 'Administration → Zones : les couleurs KMZ s\'affichent correctement (conversion du format Google Earth vers CSS)', scope: 'internal' },
      'Postes SIP extranet : affichage optimisé avec chargement complet pour la vue en cartes',
      { text: 'Administration → Hébergement : recherche par username/client, tri par colonnes, icônes de ressources avec compteurs', scope: 'internal' },
      { text: 'Fiche compte → Hébergement : le résumé (domaines web, boîtes mail, bases de données) affiche les bonnes valeurs', scope: 'internal' },
      'Annuaires extranet : noms des contacts corrigés, pagination (30, 50, 100, 200), tri par colonnes et recherche globale',
    ],
    fixes: [
      { text: 'Création de compte SIP : le message d\'erreur indique clairement quand un compte existe déjà', scope: 'internal' },
    ],
  },
  {
    version: '2.3.1',
    date: '2026-03-27',
    title: 'Hébergement, statistiques mobiles et améliorations extranet',
    features: [
      {
        title: 'Hébergement',
        scope: 'internal',
        items: [
          'Nouvel onglet « Hébergement » dans la fiche client : vue d\'ensemble, gestion des domaines web, messagerie (boîtes mail), et DNS',
          'Administration globale de l\'hébergement : gestion des serveurs HestiaCP, profils d\'hébergement et comptes depuis une page dédiée',
          'Gestion des packages d\'hébergement : création, modification et suppression directement depuis l\'interface d\'administration',
          'Changement de package d\'un compte hébergement en un clic depuis l\'onglet Comptes',
          'Gestion complète d\'un compte hébergement : domaines web (ajout, suppression, SSL, aliases), messagerie (domaines + boîtes mail), zones DNS multiples, bases de données',
        ],
      },
      'Gestion des boîtes mail en autonomie depuis l\'espace client (création, mot de passe, quota)',
      {
        title: 'Statistiques Mobiles',
        scope: 'internal',
        items: [
          'Refonte complète : vue d\'ensemble avec 6 indicateurs clés (marge, lignes, CA, coûts, taux de marge, potentiel de renégociation), graphiques de répartition par fournisseur',
          'Nouvel onglet « Analyse de flotte » : vue groupée par client avec nombre de lignes, marge, taux de marge, lignes en déficit et potentiel de renégociation',
          'Onglet « Détail par ligne » : tableau complet avec popover d\'information, taux de marge par ligne, tri sur toutes les colonnes',
          'Statistiques pré-calculées chaque nuit pour un affichage instantané',
        ],
      },
      { text: 'Gestion des utilisateurs extranet : possibilité d\'affecter ou détacher un utilisateur d\'un compte, filtre par rattachement, statistiques rapides', scope: 'internal' },
    ],
    improvements: [
      'Nouvelle présentation des postes téléphoniques en cartes avec compteurs en ligne/hors ligne',
      { text: 'Tableau de bord CA : cliquer sur une barre du graphique d\'évolution affiche les statistiques de la période correspondante', scope: 'internal' },
      { text: 'Administration → Types de lien : tableau enrichi avec colonnes débit, burst, QoS, GTI/GTR, zone et fournisseur', scope: 'internal' },
    ],
    fixes: [
      'Correction de l\'affichage des dates (mois/année) sur plusieurs pages',
      { text: 'Éditeur de plans d\'appel : les blocs de type inconnu ne sont plus supprimés lors de l\'enregistrement', scope: 'internal' },
      { text: 'Services : possibilité d\'éditer un service depuis la fiche du compte parent, même s\'il appartient à un compte enfant', scope: 'internal' },
      { text: 'Résiliation de service : la suppression du fichier fonctionne désormais quelle que soit la casse de l\'extension', scope: 'internal' },
      { text: 'Recherche de facture par référence complète (avec tiret) corrigée', scope: 'internal' },
      { text: 'Onglet Prélèvements de la fiche client accessible correctement', scope: 'internal' },
      { text: 'Onglet Facturation : le nom du commercial s\'affiche correctement au lieu de son identifiant numérique', scope: 'internal' },
    ],
  },
  {
    version: '2.2.6',
    date: '2026-03-23',
    title: 'Édition plans d\'appel, permissions réseau et CDR extranet',
    features: [
      { text: 'Possibilité d\'éditer un plan d\'appel depuis n\'importe quelle version de l\'historique (pas uniquement la version en production)', scope: 'internal' },
      'La consommation mobile dans l\'espace client affiche désormais le détail complet (graphiques journaliers, correspondants, destinations)',
    ],
    fixes: [
      { text: 'Les techniciens peuvent à nouveau ajouter des adresses IP aux services de type lien d\'accès L3', scope: 'internal' },
      { text: 'Nettoyage des permissions réseau obsolètes sur le contrôleur IP', scope: 'internal' },
      'Correction de l\'affichage des données de consommation mobile en mode extranet',
    ]
  },
  {
    version: '2.2.5',
    date: '2026-03-20',
    title: 'Refonte extranet et correctifs permissions',
    features: [
      'Nouvelle interface « Mes Services » pour les utilisateurs extranet : services regroupés par usage (Communiquer, Connecter, Matériel)',
      'Consultation de la consommation mobile depuis l\'espace client extranet',
      'Nouvelle présentation des postes téléphoniques et annuaires pour les utilisateurs extranet',
      { text: 'Les logs d\'audit affichent désormais le nom du compte concerné, avec un lien cliquable vers la fiche', scope: 'internal' },
    ],
    fixes: [
      'Les factures s\'affichent à nouveau dans l\'espace client extranet',
      { text: 'Les techniciens ayant le droit « Gérer les annuaires » peuvent maintenant ajouter, modifier, importer et exporter des contacts', scope: 'internal' },
      { text: 'Les techniciens ayant le droit « Gérer les plans d\'appel » peuvent maintenant consulter l\'historique des versions', scope: 'internal' },
      { text: 'Correction de l\'authentification par clé API en mode Bearer', scope: 'internal' },
      { text: 'Les notes de version affichent correctement les entrées internes', scope: 'internal' },
    ]
  },
  {
    version: '2.2.4',
    date: '2026-03-18',
    title: 'Correctifs et accès extranet',
    features: [
      { text: 'Les justificatifs de résiliation sont désormais stockés dans les documents du compte client (catégorie « Justificatifs ») au lieu d\'un dossier interne non accessible', scope: 'internal' },
    ],
    fixes: [
      'Les utilisateurs extranet peuvent à nouveau accéder à la vue de leur compte',
      { text: 'Éditeur de plan d\'appel : la sauvegarde en mode JSON n\'est plus bloquée par les erreurs de validation visuelle', scope: 'internal' },
      { text: 'Éditeur de plan d\'appel : correction d\'une erreur serveur lors de la sauvegarde d\'un plan existant', scope: 'internal' },
      { text: 'Création de service : la saisie du numéro de VLAN ne provoque plus de clignotement ni de perte de focus', scope: 'internal' },
      { text: 'Interface crédits eCampaign restaurée', scope: 'internal' },
    ],
  },
  {
    version: '2.2.3',
    date: '2026-03-17',
    title: 'Correctifs recherche et stabilité',
    features: [],
    fixes: [
      'Barre de recherche : l\'autocomplete des factures affiche à nouveau les résultats',
      { text: 'Correction de l\'endpoint API des factures (clients → comptes)', scope: 'internal' },
      'Correctifs divers de stabilité',
    ],
  },
  {
    version: '2.2.2',
    date: '2026-03-13',
    title: 'Correctifs services, dates et stabilité générale',
    features: [
      { text: 'Déduplication des issues GitLab : détection automatique des doublons avant création, avec lien vers l\'issue existante', scope: 'internal' },
      { text: 'Onglet « Commerciaux » (Primes) dans le dashboard CA : tableau dédié au calcul des primes avec montants M/M-1/M-2 et détail par client', scope: 'internal' },
    ],
    improvements: [
      { text: 'Historique unifié : nouvel onglet dans la fiche compte pour visualiser tous les événements (services, contacts, factures, numéros, notes) en un seul endroit', scope: 'internal' },
      { text: 'Journal d\'audit : filtre par utilisateur pour retrouver rapidement les actions d\'une personne', scope: 'internal' },
      'Accès extranet : l\'édition ne montre que les droits d\'accès et le statut. L\'email est géré depuis la fiche contact',
    ],
    fixes: [
      { text: 'Dates dans l\'édition de services : les dates de mise en service et de prochaine facturation s\'affichent désormais correctement dans tous les formulaires de modification', scope: 'internal' },
      { text: 'Upload de justificatif (résiliation numéro) : les fichiers PDF sont acceptés correctement', scope: 'internal' },
      { text: 'Éditeur de plan d\'appel : les blocs non reconnus ne sont plus supprimés silencieusement à l\'ouverture en mode visuel', scope: 'internal' },
      'Boutons « Copier » sur les fiches compte : le clic ne provoque plus d\'erreur',
      { text: 'Graphe Répartition (CA) : la bulle d\'information au survol passe correctement par-dessus les autres éléments', scope: 'internal' },
    ],
  },
  {
    version: '2.2.1',
    date: '2026-03-11',
    title: 'Harmonisation visuelle & Responsive',
    features: [
      'Barre de chargement turquoise en haut de page lors de la navigation entre les écrans',
      'Favicon et titre d\'onglet Prosoluce (remplace « Vue App » et l\'icône par défaut)',
    ],
    improvements: [
      'Meilleur affichage sur petits écrans (laptop demi-écran, tablettes)',
      'Harmonisation visuelle des tableaux et en-têtes de page',
      { text: 'Gestion tarifaire et gestion des numéros fixes : onglets redessinés avec barre de navigation bleu foncé, badges compacts, meilleure lisibilité', scope: 'internal' },
      'Mode sombre : meilleur contraste sur les en-têtes de page, le fil d\'Ariane, les profils utilisateur, les annuaires et factures',
      'Page « 404 » et « 401 » : affichage adapté mobile/tablette et meilleur rendu en mode sombre',
    ],
    fixes: [
      { text: 'Édition des rôles utilisateurs internes : la sélection d\'un rôle ne ferme plus la fenêtre, et tous les rôles sont désormais affichés dans la liste', scope: 'internal' },
      { text: 'L\'onglet « Documents » est désormais masqué (au lieu de grisé) pour les utilisateurs sans permission', scope: 'internal' },
    ],
  },
  {
    version: '2.2.0',
    date: '2026-03-10',
    title: 'Dashboard CA & Gestion Tarifaire',
    features: [
      {
        title: 'Dashboard CA',
        scope: 'internal',
        items: [
          'Les montants affichent désormais le CA total (abo + conso) avec détail au survol',
          'En mode annuel, le graphe agrège par année (et non plus par mois)',
          'Refonte en flux unique (filtres → KPI → graphes → tableau) sans sections artificielles',
          'Sélecteur de profondeur historique (N-2, N-3, N-5) pour le tableau comparatif',
          'Colonnes dynamiques dans le tableau (nombre de périodes passées configurable)',
          'Colonne N en premier, mise en évidence, avec variations inline dans les colonnes passées',
          'Pastille de période sur les KPI cards et le donut de répartition',
          'État vide sur le donut (anneau gris + "Aucune donnée") quand pas de données',
        ],
      },
      'Navbar : bouton « Notes de version » pour consulter l\'historique complet des releases à tout moment',
    ],
    improvements: [
      'Recherche navbar : tri par pertinence : les correspondances exactes du nom remontent en priorité devant les matchs partiels (tel, mail, etc.)',
    ],
    fixes: [
      'Sidebar : correction du débordement de texte lorsque le menu latéral est réduit (textes masqués, icônes centrées)',
      { text: 'Dashboard CA : le filtre Mensuelle/Annuelle contrôle la granularité d\'affichage (et non plus le type de contrat)', scope: 'internal' },
      { text: 'Dashboard CA : correction du double appel API évolution lors du changement de périodicité', scope: 'internal' },
      { text: 'Dashboard CA : label de période annuelle changé de "A" à "N" (convention française)', scope: 'internal' },
      { text: 'Dashboard CA : labels du graphe en français abrégé, correction du chevauchement sur longues périodes', scope: 'internal' },
      { text: 'Dashboard CA : colonnes du tableau en min-width flexible (plus de chevauchement)', scope: 'internal' },
      { text: 'Gestion Tarifaire : réactivation du menu dans la sidebar', scope: 'internal' },
      { text: 'Gestion Tarifaire : correction du crash au chargement du dashboard (error handling manquant sur les appels API)', scope: 'internal' },
      { text: 'Gestion Tarifaire : uniformisation du design (AdminTable, PageHeader, icônes MDI) sur le dashboard et les pages de détail', scope: 'internal' },
      { text: 'Gestion Tarifaire : pagination par défaut passée de 5 à 50 éléments', scope: 'internal' },
      { text: 'Gestion Tarifaire : correction du bouton Sauvegarder non fonctionnel sur la page EditGrid', scope: 'internal' },
      { text: 'Gestion Tarifaire : ajout du responsive (breakpoints xs/sm/md) sur le dashboard', scope: 'internal' },
      { text: 'Gestion Tarifaire : déblocage des endpoints backend (GridToProduct, Family) qui renvoyaient systématiquement 404', scope: 'internal' },
    ],
  },
  {
    version: '2.1.0',
    date: '2026-03-10',
    title: 'Refontes majeures & Sécurité',
    features: [
      {
        title: 'Interface générale',
        items: [
          'Page d\'accueil : refonte complète avec bandeau de bienvenue et grille de tuiles d\'accès rapide respectant les permissions',
          'Barre latérale : auto-repli sur écrans < 1200px',
          'Profil utilisateur : mise en page responsive',
          'Signalement de bug : formulaire intégré pour remonter les problèmes',
        ],
      },
      {
        title: 'Comptes & Services',
        scope: 'internal',
        items: [
          'Services : nouveau mode d\'affichage "vue compacte" (tableau dense)',
          'Comptes multi-sites : organigramme des sites intégré au dashboard avec popover KPI',
          'Détail de compte : section "Sites liés" avec liste tabulaire et tags visuels',
        ],
      },
      {
        title: 'Plan d\'appel',
        scope: 'internal',
        items: [
          'Éditeur : refonte architecture "defineBlock" : composants génériques remplaçant 10 composants individuels',
          'Protection contre l\'écrasement : mode JSON-only automatique pour les plans avec blocs non supportés',
          'Import de fichiers audio directement depuis les blocs sans quitter l\'éditeur',
          'Bouton "Dupliquer" un plan d\'appel',
          'Popover au survol de la colonne "Numéros" affichant les numéros liés',
        ],
      },
      {
        title: 'Administration & Droits',
        scope: 'internal',
        items: [
          'Gestion des droits : refonte visuelle (onglets, SectionHeader, descriptions inline, filtre par catégorie)',
          'Gestion des droits : page unifiée avec filtre Tous / Interne / Extranet / Menu',
          'Clés API : gestion CRUD des clés serveur (création, activation, rotation, whitelist IP)',
          'Journal d\'audit : déplacé vers une entrée dédiée dans le menu Administration',
          'Nouvelle page "Utilisateurs extranet" : vue transversale des contacts avec accès extranet',
          'Ajout des nouveaux rôles : admin, chargé de compte, responsable commercial, comptable, tech réseau/téléphonie/équipements',
        ],
      },
      {
        title: 'Utilisateurs',
        scope: 'internal',
        items: [
          'Refonte de la page : SectionHeader avec recherche unifiée et filtre par rôle, formulaire CrudDialog',
          'Édition des rôles directement depuis la liste (popover avec sélecteur multiple)',
          'Regroupement en sous-menu Administration (Internes / Extranet)',
          'Filtrage des utilisateurs internes basé sur user_account_status',
        ],
      },
      {
        title: 'Dashboard CA',
        scope: 'internal',
        items: [
          'Refonte complète : 4 cartes KPI avec variations, graphique d\'évolution temporelle, camembert de répartition par type de service',
          'Tableau par chargé de compte avec drill-down clients',
          'Filtrage par chargé de compte, mois et périodicité (mensuelle/annuelle)',
        ],
      },
      {
        title: 'Sécurité & Authentification',
        items: [
          'Mot de passe oublié : pages de réinitialisation complètes',
          'Sécurité renforcée des sessions',
        ],
      },
      {
        title: 'Extranet',
        items: [
          'Module Failover : configuration de plans d\'appel de secours',
          'Refonte des dialogues de contacts et annuaires',
          'Édition SIP modernisée',
        ],
      },
    ],
    fixes: [
      'Mode sombre : correction fond de page (flash blanc au chargement)',
      'Sidebar : correction du débordement de texte en mode replié',
      { text: 'Liens d\'accès : correction affichage client, libellé service, normalisation adresses MAC', scope: 'internal' },
      { text: 'Gestion des droits : correction nom de composant dialogSeeUser, suppression eslint-disable inutiles', scope: 'internal' },
      { text: 'Permissions : correction typos et suppression permissions fantômes', scope: 'internal' },
      'Profil utilisateur : breakpoints responsive corrigés',
      { text: 'Backend : corrections FK api_keys, UserController::show(), suppression fallback clés .env', scope: 'internal' },
    ],
  },
  {
    version: '2.0.2',
    date: '2026-02-26',
    title: '',
    features: [
      'Barre latérale : info-bulle au survol des icônes en mode replié',
      'Barre latérale : largeur réduite pour un meilleur confort visuel',
      { text: 'Services mobiles : affichage du réseau HNO (Orange, SFR, Bouygues) dans la liste des services', scope: 'internal' },
    ],
    fixes: [
      'Mode sombre : correction de l\'affichage de l\'écran de connexion (logo, fond, champs de saisie)',
      'Mode sombre : le logo replié conserve désormais les bandes turquoise',
      'Comptes : affichage de tous les services (suppression de la limite de 15)',
    ],
  },
  {
    version: '2.0.1',
    date: '2026-02-18',
    title: 'Migration Vue 3 & Refonte complète',
    features: [
      {
        title: 'Interface générale',
        items: [
          'Mode sombre : basculement clair/sombre dans la barre de navigation',
          'Recherche rapide par ID client : saisir un numéro + Entrée pour accéder directement à la fiche',
          'Bouton retour en haut de page',
          'Fil d\'Ariane amélioré dans la barre de navigation',
          'Signalement de bug depuis la barre latérale',
          'Système de release notes avec notification des nouvelles versions',
        ],
      },
      {
        title: 'Comptes',
        scope: 'internal',
        items: [
          'Refonte du dashboard services avec vue par catégorie, par site ou en liste',
          'Cartes de services avec actions rapides et coûts mensuels/annuels par catégorie',
          'Système de notes complet avec éditeur riche (gras, italique, listes, blocs de code)',
          'Filtrage avancé des notes par catégorie, site, importance et recherche textuelle',
          'Regroupement chronologique des notes (Aujourd\'hui, Hier, Cette semaine, etc.)',
          'GED (Gestion Électronique de Documents) : dépôt, consultation et gestion par catégories',
          'Système client/fournisseur : un compte peut être client, fournisseur, ou les deux',
          'Dashboard dédié fournisseur avec KPIs (factures impayées, documents, dernière note)',
          'Séparation des factures émises et factures reçues pour les fournisseurs',
          'Géocodage automatique des adresses clients en arrière-plan',
        ],
      },
      {
        title: 'Plan d\'appel',
        scope: 'internal',
        items: [
          'Éditeur visuel drag-and-drop pour les plans d\'appel',
          '10 types de blocs : Service, Playback, Créneaux horaires, Menu, Appel, Appel interne, Messagerie, Route, Fax, Application',
          'Double sauvegarde : JSON Asterisk + schéma visuel du workflow',
          'Auto-layout automatique pour organiser les blocs',
          'Annuler / Rétablir les modifications + sauvegarde de brouillons',
          'Validation en temps réel avec affichage des erreurs et avertissements',
          'Bibliothèque audio intégrée (fichiers sons depuis les documents)',
          'Synchronisation automatique des fichiers sons vers les serveurs de téléphonie',
          'Historique visuel des modifications de plans d\'appel',
        ],
      },
      {
        title: 'Configuration des liens',
        scope: 'internal',
        items: [
          'Tableau des liens avec tri sur toutes les colonnes',
          'Filtrage par statut (actif/inactif) et par type de lien',
          'Recherche globale : identifiant, nom client, adresse MAC, adresse IP',
          'Colonne client avec lien direct vers la fiche du compte',
          'Colonne adresse MAC cliquable avec lien vers la fiche service',
          'Indicateur visuel des liens inactifs (ligne grisée)',
          'Détection de conflit d\'adresse MAC avec détails du lien existant',
          'Désactivation automatique des liens à la résiliation d\'un service',
          'Unicité MAC par lien actif : une même adresse MAC ne peut être active que sur un seul lien',
        ],
      },
      {
        title: 'Gestion des numéros',
        scope: 'internal',
        items: [
          'Ajout de numéros au stock par saisie unitaire, plages ou patterns personnalisés',
          'Recherche et filtrage dans le tableau des numéros',
          'Attribution de plan d\'appel depuis la vue numéro',
        ],
      },
      {
        title: 'Extranet',
        items: [
          'Module Failover : configuration de plans d\'appel de secours',
          'Refonte des dialogues de contacts et annuaires',
          'Édition SIP modernisée',
        ],
      },
      {
        title: 'Renouvellement',
        scope: 'internal',
        items: [
          'Refonte du dashboard avec indicateurs de statut',
          'Tableau de suivi des services avec filtrage par statut de renouvellement',
        ],
      },
      {
        title: 'CA Commerciaux',
        scope: 'internal',
        items: [
          'Vue globale par commercial avec agrégation du chiffre d\'affaires',
          'Navigation vers le détail clients par commercial',
        ],
      },
      {
        title: 'Mobile',
        scope: 'internal',
        items: [
          'Carte "Pays visités" avec timeline des déplacements et drapeaux',
          'Modernisation des boutons d\'action sur les services mobiles',
        ],
      },
    ],
    fixes: [
      'Correction de l\'affichage des dates dans les tooltips',
      { text: 'Résolution de toutes les erreurs ESLint pour la compatibilité Vue 3', scope: 'internal' },
      { text: 'Correction des warnings de dépréciation Element Plus', scope: 'internal' },
      { text: 'Correction de la compatibilité IDE sur les contrôleurs backend', scope: 'internal' },
      { text: 'Correction de la gestion des adresses MAC en doublon sur les liens', scope: 'internal' },
      { text: 'Correction de la recherche dans les liens (recherche cross-table client, MAC, IP)', scope: 'internal' },
    ],
  },
];

/**
 * Adapte les releases au public qui les lit.
 *
 * En interne, tout est rendu tel quel. En extranet :
 * - les strings restent visibles à l'identique ;
 * - tout ce qui porte `scope: 'internal'` disparaît, y compris à l'intérieur
 *   d'un groupe par ailleurs visible ;
 * - la clé `extranet` remplace le texte d'un item, le titre d'un groupe ou
 *   celui de la release, pour éviter les tournures internes ;
 * - un groupe vidé de ses items, puis une release vidée de ses sections,
 *   ne sont pas rendus.
 *
 * @param {Array} releases - Tableau de releases
 * @param {'internal'|'extranet'} scope - Le scope de l'utilisateur
 * @returns {Array} Releases filtrées (releases vides exclues)
 */
export function filterReleasesForScope(releases, scope) {
  // Testé sur 'internal' et non sur 'extranet' : un scope inconnu, vide ou mal orthographié
  // doit filtrer plutôt que tout livrer.
  if (scope === 'internal') return releases;

  const filterItems = (items) => {
    if (!items) return [];
    return items.reduce((kept, item) => {
      if (typeof item === 'string') {
        kept.push(item);
        return kept;
      }
      if (!item || item.scope === 'internal') return kept;

      if (Array.isArray(item.items)) {
        const subItems = filterItems(item.items);
        if (subItems.length > 0) {
          kept.push({ ...item, title: item.extranet || item.title, items: subItems });
        }
        return kept;
      }

      // Dérouler en string pour l'affichage, dans la formulation destinée au client.
      // Un objet mal formé (groupe sans items, clé mal orthographiée) est ignoré :
      // empiler undefined ferait planter le rendu, et pour les seuls clients.
      const text = item.extranet || item.text;
      if (text) kept.push(text);
      return kept;
    }, []);
  };

  return releases
    .map(release => ({
      ...release,
      title: release.extranet || release.title,
      features: filterItems(release.features),
      fixes: filterItems(release.fixes),
      improvements: filterItems(release.improvements),
    }))
    .filter(release =>
      release.features?.length > 0 ||
      release.fixes?.length > 0 ||
      release.improvements?.length > 0
    );
}

/**
 * Récupère les releases non vues par l'utilisateur
 * @returns {Array} Liste des releases non vues
 */
export function getUnseenReleases() {
  const lastSeenVersion = localStorage.getItem('app_last_seen_version');

  if (!lastSeenVersion) {
    // Première visite: ne montrer que la version actuelle
    return releases.filter(r => r.version === APP_VERSION);
  }

  // Comparer les versions pour trouver les nouvelles
  return releases.filter(r => compareVersions(r.version, lastSeenVersion) > 0);
}

/**
 * Marque la version actuelle comme vue
 */
export function markVersionAsSeen() {
  localStorage.setItem('app_last_seen_version', APP_VERSION);
}

/**
 * Compare deux versions semver (ex: '2.4.0' vs '2.3.1')
 * @returns {number} 1 si v1 > v2, -1 si v1 < v2, 0 si égales
 */
function compareVersions(v1, v2) {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

/**
 * Vérifie si des nouvelles releases doivent être affichées
 * @returns {boolean}
 */
export function hasUnseenReleases() {
  return getUnseenReleases().length > 0;
}
